import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
    Phone,
    Mail,
    MapPin,
    Cake,
    Briefcase,
    GraduationCap,
    ArrowRight,
    MessageSquare,
    CircleDot,
    ShieldCheck,
    Clock,
    Lock,
    ChevronRight,
    Plus,
    X,
    Check,
    Pencil,
    Trash2,
    Camera,
    Heart,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/ui/Avatar";
import { Card } from "@/ui/Card";
import { InteractionDetail } from "@/ui/InteractionDetail";
import { Separator } from "@/ui/Separator";
import type { DetailTab } from "@/layout/NavBar";
import {
    resolvePlatform,
    GlobeIcon,
    PLATFORMS,
    type PlatformKey,
} from "@/icons/SocialIcons";
import {
    getInitials,
    getFullName,
    getRoleDisplay,
    getAvatarColors,
    type Contact,
    type Social,
    type Phone as PhoneType,
    type Email as EmailType,
    type Address,
    type Job,
    type Education,
} from "@/lib/data";
import {
    listInteractionsForContact,
    forgetContactVoiceprint,
} from "@/hooks/useSessionProcessor";
import type { ProcessedInteraction } from "@/types/voice";
import { ContactPortrait } from "./ContactPortrait";
import { RelationshipDynamics } from "./RelationshipDynamics";

interface ContactDetailProps {
    contact: Contact;
    activeTab: DetailTab;
    onTabChange: (tab: DetailTab) => void;
    onContactChange?: (contactId: string, updates: Partial<Contact>) => void;
}

/**
 * Listens for input focus events and scrolls the focused element
 * into view so it's not covered by the iOS keyboard.
 */
function FocusScrollHelper() {
    useEffect(() => {
        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.tagName === "SELECT"
            ) {
                // Multiple scroll attempts — inputs near the bottom (education,
                // skills, tags) render late and need extra time for the keyboard
                // to settle and the DOM to update
                const scroll = () => {
                    target.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                };
                setTimeout(scroll, 100);
                setTimeout(scroll, 350);
                setTimeout(scroll, 600);
            }
        };

        document.addEventListener("focusin", handleFocusIn);
        return () => document.removeEventListener("focusin", handleFocusIn);
    }, []);

    return null;
}

/** Format birthday string for display. Handles YYYY-MM-DD and --MM-DD (yearless). */
function formatBirthday(bd: string): string {
    const MONTHS = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    if (bd.startsWith("--")) {
        // Yearless: --MM-DD
        const [mm, dd] = bd.slice(2).split("-").map(Number);
        return `${MONTHS[mm - 1]} ${dd}`;
    }
    const parts = bd.split("-").map(Number);
    if (parts.length === 3 && parts[0] > 0) {
        return `${MONTHS[parts[1] - 1]} ${parts[2]}, ${parts[0]}`;
    }
    // Fallback
    const d = new Date(bd + "T00:00:00");
    if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    }
    return bd;
}

function FavoriteNameRow({ c, onToggle }: { c: Contact; onToggle: () => void }) {
    const [animating, setAnimating] = useState(false);
    const handleClick = () => {
        setAnimating(true);
        onToggle();
        setTimeout(() => setAnimating(false), 500);
    };
    return (
        <div className="flex items-center justify-center gap-2">
            <h1 className="text-foreground text-[26px] font-light leading-tight tracking-[-0.02em]">
                {getFullName(c)}
            </h1>
            <button onClick={handleClick} className="mt-0.5">
                <Heart
                    size={18}
                    className={`transition-colors ${animating ? "heart-animate" : ""} ${
                        c.favorite
                            ? "fill-[#C9A84C] text-[#C9A84C]"
                            : "text-[rgba(255,255,255,0.2)]"
                    }`}
                />
            </button>
        </div>
    );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <p className="text-ink-3 mb-3 text-[11px] font-bold uppercase tracking-[0.15em]">
            {children}
        </p>
    );
}

// ── Quick action — supports multiple options (phone numbers, emails) ──
// @ts-ignore — kept for future use, currently action buttons are inline
function OrbitAction({
    icon: Icon,
    options,
    scheme,
    angle,
    radius,
}: {
    icon: typeof Phone;
    options: { label: string; value: string }[];
    scheme: string; // "tel" | "sms" | "mailto"
    angle: number;
    radius: number;
}) {
    const [showPicker, setShowPicker] = useState(false);
    const rad = (angle * Math.PI) / 180;
    const x = Math.sin(rad) * radius;
    const y = -Math.cos(rad) * radius;
    const s = 56;

    const handleTap = () => {
        if (options.length === 1) {
            window.location.href = `${scheme}:${options[0].value}`;
        } else {
            setShowPicker(true);
        }
    };

    return (
        <>
            <button
                onClick={handleTap}
                className="absolute flex items-center justify-center rounded-full border border-[rgba(255,255,255,0.15)] bg-[rgba(255,255,255,0.1)] shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-transform duration-200 [backdrop-filter:blur(24px)_saturate(180%)] active:scale-90"
                style={{
                    width: s,
                    height: s,
                    left: `calc(50% + ${x}px - ${s / 2}px)`,
                    top: `calc(50% + ${y}px - ${s / 2}px)`,
                }}
            >
                <Icon size={22} strokeWidth={1.6} className="text-white" />
                {options.length > 1 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[rgba(201,168,76,0.9)] text-[10px] font-bold text-black">
                        {options.length}
                    </span>
                )}
            </button>

            {/* Picker — centered modal */}
            {showPicker && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center px-8"
                    onClick={() => setShowPicker(false)}
                >
                    <div className="absolute inset-0 bg-black/60 [backdrop-filter:blur(8px)]" />
                    <div
                        className="relative z-10 w-full max-w-[300px] overflow-hidden rounded-3xl border border-[rgba(201,168,76,0.15)] bg-[rgba(22,20,16,0.97)] shadow-[0_24px_80px_rgba(0,0,0,0.7),0_0_40px_rgba(201,168,76,0.04)] [backdrop-filter:blur(40px)_saturate(180%)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="border-b border-[rgba(201,168,76,0.08)] px-5 pb-3 pt-5">
                            <p className="text-center text-[13px] font-semibold uppercase tracking-[0.15em] text-[rgba(201,168,76,0.6)]">
                                {scheme === "tel"
                                    ? "Call"
                                    : scheme === "sms"
                                      ? "Message"
                                      : "Email"}
                            </p>
                        </div>

                        {/* Options */}
                        <div className="px-4 py-2">
                            {options.map((opt, idx) => (
                                <a
                                    key={opt.value}
                                    href={`${scheme}:${opt.value}`}
                                    className={`flex items-center gap-3 py-4 active:opacity-60 ${idx < options.length - 1 ? "border-b border-[rgba(255,255,255,0.04)]" : ""}`}
                                >
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(201,168,76,0.1)]">
                                        <Icon
                                            size={16}
                                            strokeWidth={1.8}
                                            className="text-[rgba(201,168,76,0.7)]"
                                        />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[17px] font-medium tracking-wide text-white">
                                            {scheme === "tel" || scheme === "sms"
                                                ? formatPhoneDisplay(opt.value)
                                                : opt.value}
                                        </p>
                                        <p className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.1em] text-[rgba(201,168,76,0.5)]">
                                            {opt.label || "other"}
                                        </p>
                                    </div>
                                </a>
                            ))}
                        </div>

                        {/* Cancel */}
                        <div className="border-t border-[rgba(201,168,76,0.08)] px-4 py-3">
                            <button
                                onClick={() => setShowPicker(false)}
                                className="w-full rounded-2xl bg-[rgba(255,255,255,0.04)] py-3 text-[15px] font-medium text-[rgba(255,255,255,0.4)] active:bg-[rgba(255,255,255,0.08)]"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ── Social link — minimal icon only, brand-colored ──
function SocialLink({ social }: { social: Social }) {
    const platform = resolvePlatform(social.platform);
    const customPlatform = social.iconKey ? resolvePlatform(social.iconKey) : null;

    const IconComponent = platform?.icon ?? customPlatform?.icon ?? GlobeIcon;
    const color = platform?.color ?? customPlatform?.color ?? "rgba(255,255,255,0.4)";
    const href = social.url ?? platform?.buildUrl(social.handle) ?? "#";

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-[32px] w-[32px] items-center justify-center rounded-full transition-all duration-200 active:scale-90 active:opacity-60"
        >
            <IconComponent size={17} style={{ color, opacity: 0.75 }} />
        </a>
    );
}

// ── Notes section with expand/edit ──
function NotesSection({
    contact,
    onContactChange,
}: {
    contact: Contact;
    onContactChange?: (contactId: string, updates: Partial<Contact>) => void;
}) {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const hasNotes = !!contact.notes?.trim();
    const isLong = (contact.notes?.length ?? 0) > 120;

    useEffect(() => {
        if (editing && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.selectionStart = textareaRef.current.value.length;
        }
    }, [editing]);

    const startEdit = () => {
        setEditText(contact.notes || "");
        setEditing(true);
    };

    const saveEdit = () => {
        const trimmed = editText.trim();
        onContactChange?.(contact.id, { notes: trimmed });
        setEditing(false);
    };

    const cancelEdit = () => {
        setEditing(false);
    };

    return (
        <div className="px-5 pt-5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <SectionLabel>Your Notes</SectionLabel>
                    <Lock size={10} className="text-ink-4 mb-2.5" />
                </div>
                {hasNotes && !editing && (
                    <button
                        onPointerUp={startEdit}
                        className="flex items-center gap-1 pb-2"
                    >
                        <Pencil size={11} className="text-ink-4" />
                        <span className="text-ink-4 text-[11px]">Edit</span>
                    </button>
                )}
            </div>

            {editing ? (
                <div className="mt-1">
                    <textarea
                        ref={textareaRef}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full rounded-xl border border-white/5 bg-[rgba(255,255,255,0.03)] p-3 text-[14px] font-light leading-relaxed text-white/60 outline-none placeholder:text-white/15"
                        rows={5}
                        placeholder="Write notes about this person..."
                    />
                    <div className="mt-2 flex gap-2">
                        <button
                            onPointerUp={saveEdit}
                            className="flex h-[32px] items-center gap-1 rounded-full bg-[rgba(201,168,76,0.12)] px-4"
                        >
                            <Check size={13} className="text-[#C9A84C]" />
                            <span className="text-[12px] font-medium text-[#C9A84C]">
                                Save
                            </span>
                        </button>
                        <button
                            onPointerUp={cancelEdit}
                            className="flex h-[32px] items-center gap-1 rounded-full bg-white/5 px-4"
                        >
                            <span className="text-ink-4 text-[12px]">Cancel</span>
                        </button>
                    </div>
                </div>
            ) : hasNotes ? (
                <div
                    onPointerUp={isLong ? () => setExpanded(!expanded) : undefined}
                    className={isLong ? "cursor-pointer" : ""}
                >
                    <p
                        className={`text-ink-2 text-[14px] font-light leading-relaxed ${
                            !expanded && isLong ? "line-clamp-3" : ""
                        }`}
                    >
                        {contact.notes}
                    </p>
                    {isLong && (
                        <span className="text-ink-4 mt-1 block text-[12px]">
                            {expanded ? "Show less" : "Read more"}
                        </span>
                    )}
                </div>
            ) : (
                <button onPointerUp={startEdit} className="flex items-center gap-2 pt-1">
                    <Plus size={14} className="text-ink-4" />
                    <span className="text-ink-4 text-[13px] font-light">Add a note</span>
                </button>
            )}
        </div>
    );
}

// ── Overview tab: orbital profile design ──
function OverviewSection({
    c,
    onPhotoUpload,
    onToggleFavorite,
    onContactChange,
}: {
    c: Contact;
    onPhotoUpload?: (contactId: string) => void;
    onToggleFavorite?: (contactId: string) => void;
    onContactChange?: (contactId: string, updates: Partial<Contact>) => void;
}) {
    // Ring color changes based on identity state
    const ringColor =
        c.claimed && c.connectionStatus === "connected"
            ? "rgba(58,172,181,0.15)"
            : c.claimed && c.connectionStatus === "pending"
              ? "rgba(201,168,76,0.15)"
              : "rgba(201,168,76,0.12)";

    const glowColor =
        c.claimed && c.connectionStatus === "connected"
            ? "rgba(78,174,122,0.08)"
            : "rgba(201,168,76,0.06)";

    // Unique avatar gradient from their name
    const [color1, color2] = getAvatarColors(c);
    const hasPhoto = !!c.photo;

    // Build deduped trait list from all data sources (memoized)
    type TraitCat = "tag" | "skill" | "relation" | "info";
    const allTraits = useMemo(() => {
        const traits: { text: string; category: TraitCat }[] = [];
        const seen = new Set<string>();
        const add = (text: string, cat: TraitCat) => {
            const k = text.toLowerCase().trim();
            if (k && !seen.has(k)) {
                seen.add(k);
                traits.push({ text, category: cat });
            }
        };
        for (const t of c.tags) add(t, "tag");
        for (const s of c.skills) add(s, "skill");
        for (const r of c.relations ?? []) {
            if (r.name) add(`${r.label}: ${r.name}`, "relation");
        }
        const job = c.jobs.find((j) => j.current);
        if (job?.industry) add(job.industry, "info");
        return traits;
    }, [c.tags, c.skills, c.relations, c.jobs]);

    // Cycling: 3 tags at a time, step by 3
    const [cycleIdx, setCycleIdx] = useState(0);
    useEffect(() => {
        if (allTraits.length <= 3) return;
        const timer = setInterval(
            () => setCycleIdx((p) => (p + 3) % allTraits.length),
            4000
        );
        return () => clearInterval(timer);
    }, [allTraits.length]);

    const visibleTraits: { text: string; category: TraitCat }[] = useMemo(() => {
        if (allTraits.length === 0) return [];
        if (allTraits.length <= 3) return allTraits;
        const result: { text: string; category: TraitCat }[] = [];
        for (let i = 0; i < 3; i++) {
            result.push(allTraits[(cycleIdx + i) % allTraits.length]);
        }
        return result;
    }, [allTraits, cycleIdx]);

    // 3 slots: top-center, left, right — symmetrical
    // Tags render as centered rows: 1 above avatar, 2 below avatar

    const catColors: Record<TraitCat, { border: string; bg: string; text: string }> = {
        tag: {
            border: "rgba(201,168,76,0.3)",
            bg: "rgba(201,168,76,0.1)",
            text: "rgba(201,168,76,0.85)",
        },
        skill: {
            border: "rgba(99,102,241,0.3)",
            bg: "rgba(99,102,241,0.1)",
            text: "rgba(129,140,248,0.85)",
        },
        relation: {
            border: "rgba(236,72,153,0.3)",
            bg: "rgba(236,72,153,0.1)",
            text: "rgba(244,114,182,0.85)",
        },
        info: {
            border: "rgba(45,212,191,0.3)",
            bg: "rgba(45,212,191,0.1)",
            text: "rgba(45,212,191,0.85)",
        },
    };

    return (
        <>
            {/* ── CSS for tag cycling + heart ── */}
            <style>{`
                @keyframes tag-appear {
                    0% { opacity: 0; transform: scale(0.92); }
                    12% { opacity: 1; transform: scale(1); }
                    88% { opacity: 1; transform: scale(1); }
                    100% { opacity: 0; transform: scale(0.92); }
                }
                .tag-cycle { animation: tag-appear 4s ease-in-out both; }
                @keyframes float-drift {
                    0%, 100% { transform: translate(0, 0); }
                    50% { transform: translate(var(--dx), var(--dy)); }
                }
                .orb-float { animation: float-drift var(--dur) ease-in-out infinite; }
                @keyframes heart-pop {
                    0% { transform: scale(1); }
                    15% { transform: scale(1.4); }
                    30% { transform: scale(0.95); }
                    45% { transform: scale(1.15); }
                    60% { transform: scale(1); }
                }
                .heart-animate { animation: heart-pop 0.5s ease-out; }
            `}</style>

            {/* ── Profile area — clean, contained ── */}
            <div className="relative mx-auto mt-2 h-[340px] w-[340px] overflow-hidden">
                {/* Subtle dashed ring around avatar */}
                <div
                    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                    style={{
                        width: 260,
                        height: 260,
                        border: "1px dashed rgba(201,168,76,0.1)",
                    }}
                />

                {/* Floating orbs — subtle ambient particles */}
                {[
                    {
                        x: 40,
                        y: 60,
                        size: 5,
                        dx: "8px",
                        dy: "-6px",
                        dur: "7s",
                        opacity: 0.4,
                    },
                    {
                        x: 280,
                        y: 90,
                        size: 4,
                        dx: "-6px",
                        dy: "8px",
                        dur: "9s",
                        opacity: 0.3,
                    },
                    {
                        x: 60,
                        y: 260,
                        size: 4,
                        dx: "6px",
                        dy: "5px",
                        dur: "8s",
                        opacity: 0.35,
                    },
                    {
                        x: 290,
                        y: 250,
                        size: 5,
                        dx: "-7px",
                        dy: "-5px",
                        dur: "10s",
                        opacity: 0.3,
                    },
                    {
                        x: 170,
                        y: 20,
                        size: 3,
                        dx: "4px",
                        dy: "6px",
                        dur: "11s",
                        opacity: 0.25,
                    },
                ].map((orb, i) => (
                    <div
                        key={i}
                        className="orb-float pointer-events-none absolute rounded-full"
                        style={
                            {
                                left: orb.x,
                                top: orb.y,
                                width: orb.size,
                                height: orb.size,
                                background: `rgba(201,168,76,${orb.opacity})`,
                                boxShadow: `0 0 ${orb.size * 2}px rgba(201,168,76,${orb.opacity * 0.6})`,
                                "--dx": orb.dx,
                                "--dy": orb.dy,
                                "--dur": orb.dur,
                            } as React.CSSProperties
                        }
                    />
                ))}

                {/* Cycling trait tags — single row below avatar, fading pairs */}
                <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-wrap justify-center gap-2.5">
                    {visibleTraits.map((trait, i) => {
                        const cc = catColors[trait.category];
                        return (
                            <span
                                key={`${cycleIdx}-${i}`}
                                className="tag-cycle truncate rounded-full px-3.5 py-2 text-[11px] font-semibold tracking-wide"
                                style={{
                                    border: `1px solid ${cc.border}`,
                                    background: "rgba(18,18,18,0.9)",
                                    color: cc.text,
                                    maxWidth: "48%",
                                }}
                            >
                                {trait.text}
                            </span>
                        );
                    })}
                </div>

                {/* Center avatar — fills up to the outer ring (z-10, tags are z-20) */}
                <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                        <div
                            className="flex h-[200px] w-[200px] items-center justify-center rounded-full"
                            style={{
                                background: hasPhoto
                                    ? "rgba(0,0,0,0.3)"
                                    : `linear-gradient(135deg, ${color1}, ${color2})`,
                                boxShadow: `0 0 60px ${glowColor}, 0 0 120px ${glowColor}`,
                                border: `1px solid ${ringColor}`,
                            }}
                        >
                            {hasPhoto ? (
                                <Avatar className="h-[192px] w-[192px]">
                                    <AvatarImage src={c.photo} alt={getFullName(c)} />
                                    <AvatarFallback
                                        status={c.status}
                                        className="text-[52px]"
                                    >
                                        {getInitials(c)}
                                    </AvatarFallback>
                                </Avatar>
                            ) : (
                                <span className="select-none text-[56px] font-extralight tracking-wider text-white/90">
                                    {getInitials(c)}
                                </span>
                            )}
                        </div>
                        {/* Claimed badge — bottom-right */}
                        {c.claimed && c.connectionStatus === "connected" && (
                            <div className="absolute -bottom-1 -right-1 flex h-[28px] w-[28px] items-center justify-center rounded-full border-2 border-[#121212] bg-[#4EAE7A]">
                                <ShieldCheck size={14} className="text-white" />
                            </div>
                        )}
                        {c.claimed && c.connectionStatus === "pending" && (
                            <div className="absolute -bottom-1 -right-1 flex h-[28px] w-[28px] items-center justify-center rounded-full border-2 border-[#121212] bg-[#C9A84C]">
                                <Clock size={14} className="text-white" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Camera button — on avatar circle edge, top-right at 45° */}
                <button
                    onClick={() => onPhotoUpload?.(c.id)}
                    className="absolute z-30 flex h-[42px] w-[42px] items-center justify-center rounded-full border-2 border-[#121212] bg-[rgba(255,255,255,0.2)] backdrop-blur-sm transition-all active:scale-90"
                    style={{
                        left: 240 - 21,
                        top: 99 - 21,
                    }}
                >
                    <Camera size={17} className="text-white" />
                </button>
            </div>

            {/* ── Quick actions — clean horizontal row ── */}
            <div className="mt-2 flex items-center justify-center gap-5">
                {c.phones.length > 0 && (
                    <a
                        href={`tel:${c.phones[0].number}`}
                        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 shadow-lg backdrop-blur-xl transition-transform active:scale-90"
                    >
                        <Phone size={20} strokeWidth={1.6} className="text-white" />
                    </a>
                )}
                {c.emails.length > 0 && (
                    <a
                        href={`mailto:${c.emails[0].address}`}
                        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 shadow-lg backdrop-blur-xl transition-transform active:scale-90"
                    >
                        <Mail size={20} strokeWidth={1.6} className="text-white" />
                    </a>
                )}
                {c.phones.length > 0 && (
                    <a
                        href={`sms:${c.phones[0].number}`}
                        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 shadow-lg backdrop-blur-xl transition-transform active:scale-90"
                    >
                        <MessageSquare
                            size={20}
                            strokeWidth={1.6}
                            className="text-white"
                        />
                    </a>
                )}
            </div>

            {/* ── Name + heart + role + location + socials ── */}
            <div className="px-5 pt-3 text-center">
                <FavoriteNameRow c={c} onToggle={() => onToggleFavorite?.(c.id)} />
                {c.nickname && (
                    <p className="text-gold mt-0.5 text-[12px] font-medium tracking-wide">
                        &ldquo;{c.nickname}&rdquo;
                    </p>
                )}
                <p className="text-ink-3 mt-1 text-[13px] font-normal">
                    {getRoleDisplay(c)}
                </p>
                {c.currentCity && (
                    <p className="text-ink-4 mt-0.5 text-[11px] font-light">
                        {c.currentCity}
                        {c.currentState && `, ${c.currentState}`}
                    </p>
                )}
                {c.bio && (
                    <p className="text-ink-3 mx-auto mt-2 max-w-[280px] text-[12px] font-light italic leading-relaxed">
                        {c.bio}
                    </p>
                )}
            </div>

            {/* ── Social links ── */}
            {c.socials.length > 0 && (
                <div className="flex items-center justify-center gap-1 px-5 pt-4">
                    <div className="flex items-center gap-0.5 rounded-full border border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.02)] px-2 py-1.5">
                        {c.socials.map((s, i) => (
                            <SocialLink key={i} social={s} />
                        ))}
                    </div>
                </div>
            )}

            {/* ── Your move ── */}
            {c.move && (
                <div className="px-5 pt-5">
                    <Card warm={c.status === "attention"}>
                        <p className="text-warm mb-1 text-[10px] font-bold uppercase tracking-[0.15em]">
                            Your move
                        </p>
                        <p className="text-foreground text-[14px] leading-relaxed">
                            {c.move}
                        </p>
                    </Card>
                </div>
            )}

            {/* ── Path ── */}
            {c.path && (
                <div className="px-5 pt-4">
                    <SectionLabel>Path</SectionLabel>
                    <div className="flex items-center">
                        {c.path.map((name, i) => (
                            <div key={i} className="flex items-center">
                                <div
                                    className={`rounded-pill px-4 py-1.5 text-[13px] font-semibold ${
                                        i === 0
                                            ? "shadow-gold-glow bg-gradient-to-br from-[#C9A84C] to-[#8A7340] text-white"
                                            : "border-gold-border bg-card text-ink-2 shadow-meridian border-[1.5px]"
                                    }`}
                                >
                                    {name.split(" ")[0]}
                                </div>
                                {i < c.path!.length - 1 && (
                                    <ArrowRight
                                        size={14}
                                        className="text-gold-light mx-1"
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Relationship dynamics + accumulated portrait ── */}
            <div className="px-5 pt-4">
                <RelationshipDynamics
                    contactId={c.appleContactId}
                    firstName={c.firstName}
                />
                <ContactPortrait
                    contactId={c.appleContactId}
                    firstName={c.firstName}
                />
            </div>

            {/* ── Notes (always private — your edge data) ── */}
            <NotesSection contact={c} onContactChange={onContactChange} />
        </>
    );
}

// ── Swipe-to-reveal wrapper (iOS style) ──
function SwipeRow({
    children,
    onEdit,
    onDelete,
    enabled,
}: {
    children: React.ReactNode;
    onEdit?: () => void;
    onDelete?: () => void;
    enabled?: boolean;
}) {
    const [offset, setOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    const startX = useRef(0);
    const currentOffset = useRef(0);
    const ACTION_WIDTH = 120; // total width of both buttons

    if (!enabled) return <>{children}</>;

    return (
        <div className="relative overflow-hidden">
            {/* Action buttons behind */}
            <div
                className="absolute right-0 top-0 flex h-full items-stretch"
                style={{ width: ACTION_WIDTH }}
            >
                <button
                    onClick={() => {
                        setOffset(0);
                        onEdit?.();
                    }}
                    className="flex w-[60px] items-center justify-center bg-[rgba(91,155,213,0.2)]"
                >
                    <Pencil size={16} className="text-[#5B9BD5]" />
                </button>
                <button
                    onClick={() => {
                        setOffset(0);
                        onDelete?.();
                    }}
                    className="flex w-[60px] items-center justify-center bg-[rgba(232,103,90,0.2)]"
                >
                    <Trash2 size={16} className="text-[#E8675A]" />
                </button>
            </div>
            {/* Sliding content */}
            <div
                className="relative z-10 bg-[#1a1a1a]"
                style={{
                    transform: `translateX(${offset}px)`,
                    transition: dragging
                        ? "none"
                        : "transform 0.25s cubic-bezier(0.32,0.72,0,1)",
                }}
                onTouchStart={(e) => {
                    startX.current = e.touches[0].clientX;
                    currentOffset.current = offset;
                    setDragging(true);
                }}
                onTouchMove={(e) => {
                    const dx = e.touches[0].clientX - startX.current;
                    const next = Math.min(
                        0,
                        Math.max(-ACTION_WIDTH, currentOffset.current + dx)
                    );
                    setOffset(next);
                }}
                onTouchEnd={() => {
                    setDragging(false);
                    // Snap open or closed
                    setOffset(offset < -ACTION_WIDTH / 2 ? -ACTION_WIDTH : 0);
                }}
            >
                {children}
            </div>
        </div>
    );
}

// ── Colored detail row — tappable for editing or linking ──
function DetailRow({
    icon: Icon,
    iconColor,
    iconBg,
    value,
    sub,
    label,
    editable,
    primary,
    href,
}: {
    icon: typeof Phone;
    iconColor: string;
    iconBg: string;
    value: string;
    sub?: string;
    label?: string;
    editable?: boolean;
    primary?: boolean;
    href?: string;
}) {
    const Tag = href ? "a" : "div";
    const linkProps = href
        ? {
              href,
              target: href.startsWith("http") ? ("_blank" as const) : undefined,
              rel: href.startsWith("http") ? "noopener noreferrer" : undefined,
          }
        : {};
    return (
        <Tag
            {...linkProps}
            className={`flex items-center gap-3 px-3 py-2.5 ${href || editable ? "active:bg-[rgba(255,255,255,0.04)]" : ""}`}
        >
            <div
                className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px]"
                style={{ backgroundColor: iconBg }}
            >
                <Icon size={16} style={{ color: iconColor }} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <p className="text-foreground text-[14px] leading-tight">{value}</p>
                    {primary && (
                        <span className="rounded-full bg-[rgba(78,174,122,0.12)] px-1.5 py-[1px] text-[8px] font-bold uppercase tracking-wider text-[#4EAE7A]">
                            Primary
                        </span>
                    )}
                </div>
                {sub && <p className="text-ink-4 mt-0.5 text-[11px]">{sub}</p>}
            </div>
            {label && (
                <span className="text-ink-4 shrink-0 text-[10px] font-medium uppercase tracking-wide">
                    {label}
                </span>
            )}
        </Tag>
    );
}

// ── Social detail row with branded icon ──
function SocialDetailRow({ social }: { social: Social }) {
    const platform = resolvePlatform(social.platform);
    const IconComponent = platform?.icon ?? GlobeIcon;
    const color = platform?.color ?? "rgba(255,255,255,0.5)";
    const platformLabel = platform?.label ?? social.platform;
    const href = social.url ?? platform?.buildUrl(social.handle) ?? "#";

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 active:bg-[rgba(255,255,255,0.04)]"
        >
            <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[rgba(255,255,255,0.06)]">
                <IconComponent size={16} style={{ color }} />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-foreground text-[14px] leading-tight">
                    {social.handle}
                </p>
            </div>
            <span className="text-ink-4 shrink-0 text-[10px] font-medium uppercase tracking-wide">
                {platformLabel}
            </span>
        </a>
    );
}

// ── Phone number normalizer → E.164 ──
function normalizePhone(raw: string): string {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    if (digits.startsWith("0")) return `+${digits.slice(1)}`;
    if (raw.startsWith("+")) return `+${digits}`;
    return `+${digits}`;
}

function formatPhoneDisplay(e164: string): string {
    const d = e164.replace(/\D/g, "");
    if (d.length === 11 && d.startsWith("1")) {
        return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
    }
    return e164;
}

function isValidEmail(v: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function isValidPhone(v: string): boolean {
    const digits = v.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
}

// ── Step-by-step phone/email form ──
function AddContactField({
    onAdd,
    onCancel,
    initial,
}: {
    onAdd: (type: "phone" | "email", value: string, label: string) => void;
    onCancel: () => void;
    initial?: { type: "phone" | "email"; value: string; label: string };
}) {
    const [step, setStep] = useState(0);
    const [fieldType, setFieldType] = useState<"phone" | "email">(
        initial?.type ?? "phone"
    );
    const [value, setValue] = useState(initial?.value ?? "");
    const [label, setLabel] = useState(initial?.label ?? "mobile");
    const inputRef = useRef<HTMLInputElement>(null);
    const TOTAL = 3; // 0: type, 1: value, 2: label

    useEffect(() => {
        if (step === 1) setTimeout(() => inputRef.current?.focus(), 150);
    }, [step]);

    // If editing, skip to value step
    useEffect(() => {
        if (initial) setStep(1);
    }, [initial]);

    const isValid = fieldType === "phone" ? isValidPhone(value) : isValidEmail(value);

    const finish = useCallback(
        (overrideLabel?: string) => {
            if (!isValid) return;
            const finalValue =
                fieldType === "phone"
                    ? normalizePhone(value)
                    : value.trim().toLowerCase();
            onAdd(fieldType, finalValue, overrideLabel ?? label);
        },
        [fieldType, value, label, isValid, onAdd]
    );

    const next = () => {
        if (step < TOTAL - 1) setStep(step + 1);
        else finish();
    };

    const canNext = step === 0 ? true : step === 1 ? isValid : true;

    const phoneLabels = [
        { key: "mobile", label: "Mobile" },
        { key: "work", label: "Work" },
        { key: "home", label: "Home" },
    ];
    const emailLabels = [
        { key: "personal", label: "Personal" },
        { key: "work", label: "Work" },
    ];
    const labelOptions = fieldType === "phone" ? phoneLabels : emailLabels;
    const labels = [
        "Phone or email?",
        fieldType === "phone" ? "Phone number?" : "Email address?",
        "Label?",
    ];
    const color = fieldType === "phone" ? "#5B9BD5" : "#3AACB5";

    return (
        <div className="px-3 py-3">
            <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    {fieldType === "phone" ? (
                        <Phone size={13} style={{ color }} />
                    ) : (
                        <Mail size={13} style={{ color }} />
                    )}
                    <p className="text-[12px] font-medium" style={{ color }}>
                        {initial ? "Edit" : "Add contact"}
                    </p>
                </div>
                <button
                    onClick={onCancel}
                    className="flex h-[26px] w-[26px] items-center justify-center rounded-full active:bg-[rgba(255,255,255,0.06)]"
                >
                    <X size={14} className="text-ink-4" />
                </button>
            </div>
            <StepFlow steps={TOTAL} currentStep={step}>
                <p className="text-ink-3 mb-2.5 text-[13px] font-light">{labels[step]}</p>

                {/* Step 0: Phone or Email */}
                {step === 0 && (
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setFieldType("phone");
                                setLabel("mobile");
                                setTimeout(() => next(), 150);
                            }}
                            className="flex h-[44px] flex-1 items-center justify-center gap-2 rounded-xl text-[14px] font-medium transition-all active:scale-95"
                            style={{
                                backgroundColor:
                                    fieldType === "phone"
                                        ? "rgba(91,155,213,0.15)"
                                        : "rgba(255,255,255,0.04)",
                                color:
                                    fieldType === "phone"
                                        ? "#5B9BD5"
                                        : "rgba(255,255,255,0.3)",
                                border:
                                    fieldType === "phone"
                                        ? "1px solid rgba(91,155,213,0.2)"
                                        : "1px solid rgba(255,255,255,0.06)",
                            }}
                        >
                            <Phone size={16} /> Phone
                        </button>
                        <button
                            onClick={() => {
                                setFieldType("email");
                                setLabel("personal");
                                setTimeout(() => next(), 150);
                            }}
                            className="flex h-[44px] flex-1 items-center justify-center gap-2 rounded-xl text-[14px] font-medium transition-all active:scale-95"
                            style={{
                                backgroundColor:
                                    fieldType === "email"
                                        ? "rgba(58,172,181,0.15)"
                                        : "rgba(255,255,255,0.04)",
                                color:
                                    fieldType === "email"
                                        ? "#4EAE7A"
                                        : "rgba(255,255,255,0.3)",
                                border:
                                    fieldType === "email"
                                        ? "1px solid rgba(58,172,181,0.2)"
                                        : "1px solid rgba(255,255,255,0.06)",
                            }}
                        >
                            <Mail size={16} /> Email
                        </button>
                    </div>
                )}

                {/* Step 1: Value */}
                {step === 1 && (
                    <div>
                        <input
                            ref={inputRef}
                            type={fieldType === "phone" ? "tel" : "email"}
                            inputMode={fieldType === "phone" ? "tel" : "email"}
                            autoComplete={fieldType === "phone" ? "tel" : "email"}
                            placeholder={
                                fieldType === "phone"
                                    ? "(201) 555-0100"
                                    : "name@example.com"
                            }
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && isValid) next();
                            }}
                            className={`w-full ${darkInput} focus:border-[${color}40]`}
                        />
                        {value.length > 0 && !isValid && (
                            <p className="mt-1.5 text-[11px] text-[rgba(232,103,90,0.7)]">
                                {fieldType === "phone"
                                    ? "Enter a valid phone number"
                                    : "Enter a valid email"}
                            </p>
                        )}
                        {value.length > 0 && isValid && fieldType === "phone" && (
                            <p className="mt-1.5 text-[11px] text-[rgba(78,174,122,0.6)]">
                                {formatPhoneDisplay(normalizePhone(value))}
                            </p>
                        )}
                    </div>
                )}

                {/* Step 2: Label */}
                {step === 2 && (
                    <div className="flex flex-wrap gap-1.5">
                        {labelOptions.map((l) => (
                            <button
                                key={l.key}
                                onClick={() => {
                                    setLabel(l.key);
                                    setTimeout(() => finish(l.key), 150);
                                }}
                                className="h-[36px] rounded-xl px-4 text-[13px] font-medium transition-all active:scale-95"
                                style={{
                                    backgroundColor:
                                        label === l.key
                                            ? `${color}25`
                                            : "rgba(255,255,255,0.04)",
                                    color:
                                        label === l.key ? color : "rgba(255,255,255,0.3)",
                                    border:
                                        label === l.key
                                            ? `1px solid ${color}35`
                                            : "1px solid rgba(255,255,255,0.06)",
                                }}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                )}
            </StepFlow>
            <div className="mt-3 flex items-center gap-2">
                {step > 0 && (
                    <button
                        onClick={() => setStep(step - 1)}
                        className="flex h-[36px] items-center rounded-xl border border-[rgba(255,255,255,0.06)] px-3 text-[12px] text-[rgba(255,255,255,0.35)] active:bg-[rgba(255,255,255,0.04)]"
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={step === TOTAL - 1 ? () => finish() : next}
                    disabled={!canNext}
                    className="flex h-[36px] flex-1 items-center justify-center gap-1.5 rounded-xl transition-all active:scale-[0.98]"
                    style={{
                        backgroundColor: canNext
                            ? `${color}2d`
                            : "rgba(255,255,255,0.03)",
                        border: canNext
                            ? `1px solid ${color}35`
                            : "1px solid rgba(255,255,255,0.05)",
                    }}
                >
                    {step === TOTAL - 1 ? (
                        <>
                            <Check
                                size={14}
                                style={{
                                    color: canNext ? color : "rgba(255,255,255,0.12)",
                                }}
                            />
                            <span
                                className="text-[13px] font-medium"
                                style={{
                                    color: canNext ? color : "rgba(255,255,255,0.12)",
                                }}
                            >
                                {initial ? "Save" : "Done"}
                            </span>
                        </>
                    ) : (
                        <span
                            className="text-[13px] font-medium"
                            style={{
                                color: canNext ? color : "rgba(255,255,255,0.12)",
                            }}
                        >
                            Next
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}

// ── Auto-detect platform from URL or handle ──
function detectPlatform(input: string): PlatformKey | null {
    const lower = input.toLowerCase();
    // Domain detection from pasted URLs
    const domainMap: [string, PlatformKey][] = [
        ["linkedin.com", "linkedin"],
        ["twitter.com", "twitter"],
        ["x.com", "x"],
        ["instagram.com", "instagram"],
        ["facebook.com", "facebook"],
        ["fb.com", "facebook"],
        ["tiktok.com", "tiktok"],
        ["youtube.com", "youtube"],
        ["youtu.be", "youtube"],
        ["github.com", "github"],
        ["discord.gg", "discord"],
        ["discord.com", "discord"],
        ["reddit.com", "reddit"],
        ["snapchat.com", "snapchat"],
        ["wa.me", "whatsapp"],
        ["t.me", "telegram"],
        ["threads.net", "threads"],
        ["open.spotify.com", "spotify"],
        ["spotify.com", "spotify"],
        ["pinterest.com", "pinterest"],
        ["twitch.tv", "twitch"],
        ["vimeo.com", "vimeo"],
        ["slack.com", "slack"],
        ["venmo.com", "venmo"],
        ["cash.app", "cashapp"],
        ["linktr.ee", "linkedin"], // won't match — handled as custom
    ];
    for (const [domain, key] of domainMap) {
        if (lower.includes(domain)) return key;
    }
    return null;
}

// Extract handle from a full URL
function extractHandle(url: string, _platform: PlatformKey): string {
    try {
        const u = new URL(url.startsWith("http") ? url : `https://${url}`);
        const path = u.pathname.replace(/^\/+|\/+$/g, "");
        // Strip common prefixes
        const cleaned = path
            .replace(/^in\//, "") // linkedin
            .replace(/^u\//, "") // reddit
            .replace(/^@/, "")
            .replace(/^user\//, ""); // spotify
        return cleaned || url;
    } catch {
        return url;
    }
}

// Popular platforms shown first in the picker
const POPULAR_PLATFORMS: PlatformKey[] = [
    "linkedin",
    "instagram",
    "x",
    "tiktok",
    "facebook",
    "github",
    "discord",
    "reddit",
    "youtube",
    "snapchat",
    "whatsapp",
    "telegram",
    "threads",
    "spotify",
    "twitch",
    "pinterest",
    "venmo",
    "cashapp",
    "slack",
    "vimeo",
    "signal",
    "bereal",
];

// ── Inline add/edit social link ──
function AddSocialField({
    onAdd,
    onCancel,
    initial,
}: {
    onAdd: (social: Social) => void;
    onCancel: () => void;
    initial?: Social;
}) {
    const initPlatform = initial
        ? resolvePlatform(initial.platform)
            ? (initial.platform.toLowerCase() as PlatformKey)
            : "custom"
        : null;
    const [selectedPlatform, setSelectedPlatform] = useState<
        PlatformKey | "custom" | null
    >(initPlatform);
    const [value, setValue] = useState(initial?.handle ?? initial?.url ?? "");
    const [customLabel, setCustomLabel] = useState(initial?.label ?? "");
    const inputRef = useRef<HTMLInputElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Don't auto-focus until platform selected
        if (selectedPlatform) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [selectedPlatform]);

    // Auto-detect platform from typed/pasted URL
    useEffect(() => {
        if (selectedPlatform === "custom" || !value) return;
        const detected = detectPlatform(value);
        if (detected && detected !== selectedPlatform) {
            setSelectedPlatform(detected);
        }
    }, [value, selectedPlatform]);

    const platform =
        selectedPlatform && selectedPlatform !== "custom"
            ? PLATFORMS[selectedPlatform]
            : null;

    const isUrl = value.includes(".") || value.includes("://");
    const isValid = value.trim().length > 1;

    const handleSubmit = useCallback(() => {
        if (!isValid) return;
        const trimmed = value.trim();

        if (selectedPlatform && selectedPlatform !== "custom") {
            const handle = isUrl
                ? extractHandle(trimmed, selectedPlatform)
                : trimmed.replace(/^@/, "");
            onAdd({
                platform: selectedPlatform,
                handle,
                url: isUrl
                    ? trimmed.startsWith("http")
                        ? trimmed
                        : `https://${trimmed}`
                    : undefined,
            });
        } else {
            // Custom link
            onAdd({
                platform: "custom",
                handle: customLabel || "Link",
                url: trimmed.startsWith("http") ? trimmed : `https://${trimmed}`,
                label:
                    customLabel ||
                    new URL(
                        trimmed.startsWith("http") ? trimmed : `https://${trimmed}`
                    ).hostname.replace("www.", ""),
            });
        }
    }, [value, selectedPlatform, customLabel, isUrl, isValid, onAdd]);

    // Phase 1: Platform picker (no platform selected yet)
    if (!selectedPlatform) {
        return (
            <div className="px-3 py-2">
                <div className="mb-2 flex items-center justify-between">
                    <p className="text-ink-3 text-[12px] font-medium">Choose platform</p>
                    <button
                        onClick={onCancel}
                        className="flex h-[28px] w-[28px] items-center justify-center rounded-full active:bg-[rgba(255,255,255,0.06)]"
                    >
                        <X size={16} className="text-ink-4" />
                    </button>
                </div>
                <div
                    ref={scrollRef}
                    className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    {POPULAR_PLATFORMS.map((key) => {
                        const p = PLATFORMS[key];
                        const IconComp = p.icon;
                        return (
                            <button
                                key={key}
                                onClick={() => setSelectedPlatform(key)}
                                className="flex shrink-0 flex-col items-center gap-1 rounded-xl px-2 py-2 active:bg-[rgba(255,255,255,0.06)]"
                                style={{ minWidth: 56 }}
                            >
                                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] bg-[rgba(255,255,255,0.06)]">
                                    <IconComp size={18} style={{ color: p.color }} />
                                </div>
                                <span className="text-ink-4 text-[9px] font-medium">
                                    {p.label}
                                </span>
                            </button>
                        );
                    })}
                    {/* Custom / other */}
                    <button
                        onClick={() => setSelectedPlatform("custom")}
                        className="flex shrink-0 flex-col items-center gap-1 rounded-xl px-2 py-2 active:bg-[rgba(255,255,255,0.06)]"
                        style={{ minWidth: 56 }}
                    >
                        <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border border-dashed border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]">
                            <GlobeIcon
                                size={18}
                                style={{ color: "rgba(255,255,255,0.4)" }}
                            />
                        </div>
                        <span className="text-ink-4 text-[9px] font-medium">Other</span>
                    </button>
                </div>
            </div>
        );
    }

    // Phase 2: Input for the selected platform
    const IconComp = platform?.icon ?? GlobeIcon;
    const color = platform?.color ?? "rgba(255,255,255,0.5)";
    const platformLabel = platform?.label ?? "Link";

    return (
        <div className="px-3 py-2">
            {/* Header with selected platform */}
            <div className="mb-2 flex items-center justify-between">
                <button
                    onClick={() => {
                        setSelectedPlatform(null);
                        setValue("");
                    }}
                    className="flex items-center gap-1.5 rounded-full bg-[rgba(255,255,255,0.04)] px-2.5 py-1 active:bg-[rgba(255,255,255,0.08)]"
                >
                    <IconComp size={14} style={{ color }} />
                    <span className="text-[12px] font-medium" style={{ color }}>
                        {platformLabel}
                    </span>
                    <ChevronRight
                        size={12}
                        className="rotate-180 text-[rgba(255,255,255,0.2)]"
                    />
                </button>
                <button
                    onClick={onCancel}
                    className="flex h-[28px] w-[28px] items-center justify-center rounded-full active:bg-[rgba(255,255,255,0.06)]"
                >
                    <X size={16} className="text-ink-4" />
                </button>
            </div>

            {/* Input row */}
            <div className="flex items-center gap-2">
                <div
                    className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[10px]"
                    style={{ backgroundColor: `${color}18` }}
                >
                    <IconComp size={17} style={{ color }} />
                </div>
                <input
                    ref={inputRef}
                    type="url"
                    inputMode="url"
                    placeholder={
                        selectedPlatform === "custom"
                            ? "https://linktr.ee/yourname"
                            : platform
                              ? `@handle or ${platform.buildUrl("...").replace("https://", "")}`
                              : "URL or handle"
                    }
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleSubmit();
                        if (e.key === "Escape") onCancel();
                    }}
                    className="h-[40px] min-w-0 flex-1 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.04)] px-3 text-[15px] font-light text-white placeholder:text-[rgba(255,255,255,0.2)] focus:border-[rgba(255,255,255,0.15)] focus:outline-none"
                />
                <button
                    onClick={handleSubmit}
                    disabled={!isValid}
                    className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-xl transition-all duration-150 active:scale-90"
                    style={{
                        backgroundColor: isValid
                            ? `${color}30`
                            : "rgba(255,255,255,0.04)",
                    }}
                >
                    <Check
                        size={18}
                        style={{
                            color: isValid ? color : "rgba(255,255,255,0.15)",
                        }}
                    />
                </button>
            </div>

            {/* Custom label input (for "other" links) */}
            {selectedPlatform === "custom" && (
                <div className="mt-2">
                    <input
                        type="text"
                        placeholder="Label (e.g. Linktree, Portfolio, Company)"
                        value={customLabel}
                        onChange={(e) => setCustomLabel(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") inputRef.current?.focus();
                        }}
                        className="h-[36px] w-full rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.03)] px-3 text-[13px] font-light text-white placeholder:text-[rgba(255,255,255,0.18)] focus:border-[rgba(255,255,255,0.12)] focus:outline-none"
                    />
                </div>
            )}

            {/* Preview */}
            {value.length > 0 && isValid && platform && !isUrl && (
                <p className="mt-1.5 px-1 text-[11px]" style={{ color: `${color}80` }}>
                    {platform.buildUrl(value.replace(/^@/, "")).replace("https://", "")}
                </p>
            )}
        </div>
    );
}

// ── Helper: build Google Maps URL from address ──
function buildMapsUrl(addr: Address | { city?: string; state?: string }): string {
    const parts: string[] = [];
    if ("street" in addr && addr.street) parts.push(addr.street);
    if (addr.city) parts.push(addr.city);
    if (addr.state) parts.push(addr.state);
    if ("zip" in addr && addr.zip) parts.push(addr.zip);
    return `https://maps.google.com/?q=${encodeURIComponent(parts.join(", "))}`;
}

// ── Shared dark input class ──
const darkInput =
    "h-[40px] rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.04)] px-3 text-[14px] font-light text-white placeholder:text-[rgba(255,255,255,0.2)] focus:outline-none [&:-webkit-autofill]:[-webkit-text-fill-color:white] [&:-webkit-autofill]:[box-shadow:0_0_0_40px_#1a1a1a_inset] [&:-webkit-autofill]:[-webkit-background-clip:text]";

// ── Common industries for quick-pick ──
const INDUSTRIES = [
    "Technology",
    "Healthcare",
    "Financial Services",
    "Consulting",
    "Legal",
    "Real Estate",
    "Marketing",
    "Education",
    "Retail",
    "Manufacturing",
    "Cybersecurity",
    "Media",
    "Nonprofit",
    "Government",
    "Cannabis",
    "Food & Bev",
    "Construction",
    "Other",
];

// ── Common degree types ──
const DEGREES = [
    "HS",
    "AA",
    "AS",
    "BA",
    "BS",
    "BFA",
    "MA",
    "MS",
    "MBA",
    "JD",
    "MD",
    "PhD",
    "EdD",
    "Other",
];

// ── Step flow — one question at a time with smooth transition ──
function StepFlow({
    steps,
    currentStep,
    children,
}: {
    steps: number;
    currentStep: number;
    children: React.ReactNode;
}) {
    return (
        <div className="relative overflow-hidden">
            <div className="mb-3 flex items-center justify-center gap-1.5">
                {Array.from({ length: steps }).map((_, i) => (
                    <div
                        key={i}
                        className="h-[3px] rounded-full transition-all duration-300"
                        style={{
                            width: i === currentStep ? 20 : 6,
                            backgroundColor:
                                i < currentStep
                                    ? "rgba(255,255,255,0.3)"
                                    : i === currentStep
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(255,255,255,0.08)",
                        }}
                    />
                ))}
            </div>
            <div
                key={currentStep}
                className="animate-in fade-in slide-in-from-right-4 duration-200"
            >
                {children}
            </div>
        </div>
    );
}

// ── Compact date pill with min/max validation ──
function DatePill({
    value,
    onChange,
    placeholder,
    minDate,
    maxDate,
}: {
    value: string; // "YYYY-MM"
    onChange: (v: string) => void;
    placeholder: string;
    minDate?: string; // "YYYY-MM" — can't select before this
    maxDate?: string; // "YYYY-MM" — can't select after this
}) {
    const [picking, setPicking] = useState(false);
    const currentYear = new Date().getFullYear();
    const parsedY = value ? parseInt(value.slice(0, 4), 10) : currentYear;
    const parsedM = value ? parseInt(value.slice(5, 7), 10) : 0;

    const minY = minDate ? parseInt(minDate.slice(0, 4), 10) : 0;
    const minM = minDate ? parseInt(minDate.slice(5, 7), 10) : 0;
    const maxY = maxDate ? parseInt(maxDate.slice(0, 4), 10) : 9999;
    const maxM = maxDate ? parseInt(maxDate.slice(5, 7), 10) : 12;

    function isMonthDisabled(year: number, month: number): boolean {
        const v = year * 100 + month;
        const lo = minDate ? minY * 100 + minM : 0;
        const hi = maxDate ? maxY * 100 + maxM : 999912;
        return v < lo || v > hi;
    }

    function isYearDisabled(year: number): boolean {
        if (minDate && year < minY) return true;
        if (maxDate && year > maxY) return true;
        return false;
    }

    if (!picking) {
        return (
            <button
                onClick={() => setPicking(true)}
                className="flex h-[32px] items-center rounded-lg border px-2.5 text-[12px] font-medium active:scale-95"
                style={{
                    borderColor: value
                        ? "rgba(201,168,76,0.2)"
                        : "rgba(255,255,255,0.08)",
                    backgroundColor: value
                        ? "rgba(201,168,76,0.06)"
                        : "rgba(255,255,255,0.03)",
                    color: value ? "#C9A84C" : "rgba(255,255,255,0.25)",
                }}
            >
                {value ? `${MONTHS[parsedM - 1]} ${parsedY}` : placeholder}
            </button>
        );
    }

    return (
        <div className="w-full overflow-hidden rounded-xl border border-[rgba(201,168,76,0.12)] bg-[#1a1a1a] p-2">
            {/* Year row */}
            <div className="-mx-1 mb-1.5 flex gap-0.5 overflow-x-auto px-1 pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {Array.from({ length: 40 }, (_, i) => currentYear - i).map((y) => {
                    const disabled = isYearDisabled(y);
                    return (
                        <button
                            key={y}
                            disabled={disabled}
                            onClick={() => {
                                if (disabled) return;
                                onChange(`${y}-${String(parsedM || 1).padStart(2, "0")}`);
                            }}
                            className="shrink-0 rounded px-2 py-0.5 text-[10px] font-medium"
                            style={{
                                backgroundColor:
                                    y === parsedY
                                        ? "rgba(201,168,76,0.2)"
                                        : "transparent",
                                color: disabled
                                    ? "rgba(255,255,255,0.08)"
                                    : y === parsedY
                                      ? "#C9A84C"
                                      : "rgba(255,255,255,0.25)",
                                pointerEvents: disabled ? "none" : undefined,
                            }}
                        >
                            {y}
                        </button>
                    );
                })}
            </div>
            {/* Month grid */}
            <div className="grid grid-cols-4 gap-1">
                {MONTHS.map((name, i) => {
                    const disabled = isMonthDisabled(parsedY, i + 1);
                    return (
                        <button
                            key={i}
                            disabled={disabled}
                            onClick={() => {
                                if (disabled) return;
                                onChange(
                                    `${parsedY || currentYear}-${String(i + 1).padStart(2, "0")}`
                                );
                                setPicking(false);
                            }}
                            className="rounded-lg py-1.5 text-center text-[11px] font-medium active:scale-95"
                            style={{
                                backgroundColor:
                                    i + 1 === parsedM
                                        ? "rgba(201,168,76,0.2)"
                                        : "rgba(255,255,255,0.03)",
                                color: disabled
                                    ? "rgba(255,255,255,0.08)"
                                    : i + 1 === parsedM
                                      ? "#C9A84C"
                                      : "rgba(255,255,255,0.3)",
                                pointerEvents: disabled ? "none" : undefined,
                            }}
                        >
                            {name}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ── Step-by-step career form ──
function AddJobField({
    onAdd,
    onCancel,
    initial,
}: {
    onAdd: (job: Job) => void;
    onCancel: () => void;
    initial?: Job;
}) {
    const [step, setStep] = useState(0);
    const [title, setTitle] = useState(initial?.title ?? "");
    const [company, setCompany] = useState(initial?.company ?? "");
    const [current, setCurrent] = useState(initial?.current ?? true);
    const [startDate, setStartDate] = useState(initial?.startDate ?? "");
    const [endDate, setEndDate] = useState(initial?.endDate ?? "");
    const [industry, setIndustry] = useState(initial?.industry ?? "");
    const inputRef = useRef<HTMLInputElement>(null);
    const TOTAL = current ? 5 : 6;

    useEffect(() => {
        if (step === 0 || step === 1 || step === 4) {
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [step]);

    const finish = useCallback(() => {
        if (!title.trim() || !company.trim()) return;
        onAdd({
            title: title.trim(),
            company: company.trim(),
            industry: industry.trim() || undefined,
            current,
            startDate: startDate || undefined,
            endDate: current ? undefined : endDate || undefined,
        });
    }, [title, company, industry, current, startDate, endDate, onAdd]);

    const next = () => {
        if (step < TOTAL - 1) setStep(step + 1);
        else finish();
    };

    const canNext =
        step === 0
            ? title.trim().length > 0
            : step === 1
              ? company.trim().length > 0
              : true;

    const labels = current
        ? [
              "Job title?",
              "Company?",
              "Current or past?",
              "When did you start?",
              "Industry?",
          ]
        : [
              "Job title?",
              "Company?",
              "Current or past?",
              "When did you start?",
              "When did you leave?",
              "Industry?",
          ];

    return (
        <div className="px-3 py-3">
            <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <Briefcase size={13} className="text-[#C9A84C]" />
                    <p className="text-[12px] font-medium text-[#C9A84C]">
                        {initial ? "Edit" : "Add job"}
                    </p>
                </div>
                <button
                    onClick={onCancel}
                    className="flex h-[26px] w-[26px] items-center justify-center rounded-full active:bg-[rgba(255,255,255,0.06)]"
                >
                    <X size={14} className="text-ink-4" />
                </button>
            </div>
            <StepFlow steps={TOTAL} currentStep={step}>
                <p className="text-ink-3 mb-2.5 text-[13px] font-light">{labels[step]}</p>

                {/* Step 0: Title */}
                {step === 0 && (
                    <input
                        ref={inputRef}
                        type="text"
                        autoComplete="organization-title"
                        placeholder="e.g. Sales Director, Engineer..."
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canNext && next()}
                        className={`w-full ${darkInput} focus:border-[rgba(201,168,76,0.25)]`}
                    />
                )}

                {/* Step 1: Company */}
                {step === 1 && (
                    <input
                        ref={inputRef}
                        type="text"
                        autoComplete="organization"
                        placeholder="e.g. Google, Deloitte..."
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canNext && next()}
                        className={`w-full ${darkInput} focus:border-[rgba(201,168,76,0.25)]`}
                    />
                )}

                {/* Step 2: Current/Past toggle */}
                {step === 2 && (
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setCurrent(true);
                                setTimeout(() => next(), 150);
                            }}
                            className="flex h-[44px] flex-1 items-center justify-center rounded-xl text-[14px] font-medium transition-all active:scale-95"
                            style={{
                                backgroundColor: current
                                    ? "rgba(58,172,181,0.15)"
                                    : "rgba(255,255,255,0.04)",
                                color: current ? "#4EAE7A" : "rgba(255,255,255,0.3)",
                                border: current
                                    ? "1px solid rgba(58,172,181,0.2)"
                                    : "1px solid rgba(255,255,255,0.06)",
                            }}
                        >
                            Current role
                        </button>
                        <button
                            onClick={() => {
                                setCurrent(false);
                                setTimeout(() => next(), 150);
                            }}
                            className="flex h-[44px] flex-1 items-center justify-center rounded-xl text-[14px] font-medium transition-all active:scale-95"
                            style={{
                                backgroundColor: !current
                                    ? "rgba(255,255,255,0.08)"
                                    : "rgba(255,255,255,0.04)",
                                color: !current ? "white" : "rgba(255,255,255,0.3)",
                                border: !current
                                    ? "1px solid rgba(255,255,255,0.12)"
                                    : "1px solid rgba(255,255,255,0.06)",
                            }}
                        >
                            Past role
                        </button>
                    </div>
                )}

                {/* Step 3: Start date */}
                {step === 3 && (
                    <DatePill
                        value={startDate}
                        onChange={(v) => {
                            setStartDate(v);
                            setTimeout(() => next(), 200);
                        }}
                        placeholder="Tap to pick month & year"
                        maxDate={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`}
                    />
                )}

                {/* Step 4 (past only): End date */}
                {!current && step === 4 && (
                    <DatePill
                        value={endDate}
                        onChange={(v) => {
                            setEndDate(v);
                            setTimeout(() => next(), 200);
                        }}
                        placeholder="Tap to pick month & year"
                        minDate={startDate}
                        maxDate={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`}
                    />
                )}

                {/* Last step: Industry */}
                {step === (current ? 4 : 5) && (
                    <div className="flex flex-wrap gap-1.5">
                        {INDUSTRIES.map((ind) => (
                            <button
                                key={ind}
                                onClick={() => {
                                    setIndustry(industry === ind ? "" : ind);
                                    setTimeout(() => finish(), 150);
                                }}
                                className="h-[34px] rounded-xl px-3.5 text-[12px] font-medium transition-all active:scale-95"
                                style={{
                                    backgroundColor:
                                        industry === ind
                                            ? "rgba(201,168,76,0.15)"
                                            : "rgba(255,255,255,0.04)",
                                    color:
                                        industry === ind
                                            ? "#C9A84C"
                                            : "rgba(255,255,255,0.3)",
                                    border:
                                        industry === ind
                                            ? "1px solid rgba(201,168,76,0.2)"
                                            : "1px solid rgba(255,255,255,0.06)",
                                }}
                            >
                                {ind}
                            </button>
                        ))}
                        <button
                            onClick={finish}
                            className="h-[34px] rounded-xl border border-dashed border-[rgba(255,255,255,0.1)] px-3.5 text-[12px] text-[rgba(255,255,255,0.2)]"
                        >
                            Skip
                        </button>
                    </div>
                )}
            </StepFlow>
            <div className="mt-3 flex items-center gap-2">
                {step > 0 && (
                    <button
                        onClick={() => setStep(step - 1)}
                        className="flex h-[36px] items-center rounded-xl border border-[rgba(255,255,255,0.06)] px-3 text-[12px] text-[rgba(255,255,255,0.35)] active:bg-[rgba(255,255,255,0.04)]"
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={step === TOTAL - 1 ? finish : next}
                    disabled={!canNext}
                    className="flex h-[36px] flex-1 items-center justify-center gap-1.5 rounded-xl transition-all active:scale-[0.98]"
                    style={{
                        backgroundColor: canNext
                            ? "rgba(201,168,76,0.18)"
                            : "rgba(255,255,255,0.03)",
                        border: canNext
                            ? "1px solid rgba(201,168,76,0.2)"
                            : "1px solid rgba(255,255,255,0.05)",
                    }}
                >
                    {step === TOTAL - 1 ? (
                        <>
                            <Check
                                size={14}
                                style={{
                                    color: canNext ? "#C9A84C" : "rgba(255,255,255,0.12)",
                                }}
                            />
                            <span
                                className="text-[13px] font-medium"
                                style={{
                                    color: canNext ? "#C9A84C" : "rgba(255,255,255,0.12)",
                                }}
                            >
                                {initial ? "Save" : "Done"}
                            </span>
                        </>
                    ) : (
                        <span
                            className="text-[13px] font-medium"
                            style={{
                                color: canNext ? "#C9A84C" : "rgba(255,255,255,0.12)",
                            }}
                        >
                            Next
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}

// ── Step-by-step education form ──
function AddEducationField({
    onAdd,
    onCancel,
    initial,
}: {
    onAdd: (edu: Education) => void;
    onCancel: () => void;
    initial?: Education;
}) {
    const [step, setStep] = useState(0);
    const [school, setSchool] = useState(initial?.school ?? "");
    const [degree, setDegree] = useState(initial?.degree ?? "");
    const [field, setField] = useState(initial?.field ?? "");
    const [startYear, setStartYear] = useState(initial?.startYear?.toString() ?? "");
    const [endYear, setEndYear] = useState(
        initial?.endYear?.toString() ?? initial?.year?.toString() ?? ""
    );
    const [suggestions, setSuggestions] = useState<
        Array<{ name: string; country: string }>
    >([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const currentYear = new Date().getFullYear();
    const TOTAL = 4;

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 150);
    }, [step]);

    useEffect(() => {
        if (school.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const r = await fetch(
                    `https://universities.hipolabs.com/search?name=${encodeURIComponent(school)}&limit=5`
                );
                const d = (await r.json()) as Array<{ name: string; country: string }>;
                setSuggestions(d.slice(0, 5));
                setShowSuggestions(d.length > 0);
            } catch {
                setSuggestions([]);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [school]);

    const finish = useCallback(() => {
        if (!school.trim()) return;
        const sy = parseInt(startYear, 10);
        const ey = parseInt(endYear, 10);
        onAdd({
            school: school.trim(),
            degree: degree || undefined,
            field: field.trim() || undefined,
            startYear: sy >= 1950 && sy <= currentYear + 6 ? sy : undefined,
            endYear: ey >= 1950 && ey <= currentYear + 6 ? ey : undefined,
            year: ey >= 1950 && ey <= currentYear + 6 ? ey : undefined,
        });
    }, [school, degree, field, startYear, endYear, currentYear, onAdd]);

    const next = () => {
        if (step < TOTAL - 1) setStep(step + 1);
        else finish();
    };
    const canNext = step === 0 ? school.trim().length > 0 : true;
    const labels = [
        "Where did you study?",
        "What degree?",
        "Field of study?",
        "What years?",
    ];

    return (
        <div className="px-3 py-3">
            <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <GraduationCap size={13} className="text-[#4EAE7A]" />
                    <p className="text-[12px] font-medium text-[#4EAE7A]">
                        {initial ? "Edit" : "Add education"}
                    </p>
                </div>
                <button
                    onClick={onCancel}
                    className="flex h-[26px] w-[26px] items-center justify-center rounded-full active:bg-[rgba(255,255,255,0.06)]"
                >
                    <X size={14} className="text-ink-4" />
                </button>
            </div>
            <StepFlow steps={TOTAL} currentStep={step}>
                <p className="text-ink-3 mb-2.5 text-[13px] font-light">{labels[step]}</p>

                {step === 0 && (
                    <div>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Start typing school name..."
                            value={school}
                            onChange={(e) => setSchool(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && canNext) next();
                                if (e.key === "Escape") setShowSuggestions(false);
                            }}
                            onFocus={() =>
                                suggestions.length > 0 && setShowSuggestions(true)
                            }
                            className={`w-full ${darkInput} focus:border-[rgba(78,174,122,0.25)]`}
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <div className="mt-1.5 max-h-[160px] overflow-y-auto rounded-xl border border-[rgba(78,174,122,0.12)] bg-[rgba(78,174,122,0.04)] py-1">
                                {suggestions.map((s, i) => (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setSchool(s.name);
                                            setShowSuggestions(false);
                                            setTimeout(() => next(), 100);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left active:bg-[rgba(255,255,255,0.04)]"
                                    >
                                        <GraduationCap
                                            size={12}
                                            className="shrink-0 text-[#4EAE7A]"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-[13px] text-white">
                                                {s.name}
                                            </p>
                                            <p className="text-[9px] text-[rgba(255,255,255,0.3)]">
                                                {s.country}
                                            </p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {step === 1 && (
                    <div className="flex flex-wrap gap-1.5">
                        {DEGREES.map((d) => (
                            <button
                                key={d}
                                onClick={() => {
                                    setDegree(degree === d ? "" : d);
                                    setTimeout(() => next(), 150);
                                }}
                                className="h-[36px] rounded-xl px-4 text-[13px] font-medium transition-all active:scale-95"
                                style={{
                                    backgroundColor:
                                        degree === d
                                            ? "rgba(78,174,122,0.15)"
                                            : "rgba(255,255,255,0.04)",
                                    color:
                                        degree === d
                                            ? "#4EAE7A"
                                            : "rgba(255,255,255,0.3)",
                                    border:
                                        degree === d
                                            ? "1px solid rgba(78,174,122,0.2)"
                                            : "1px solid rgba(255,255,255,0.06)",
                                }}
                            >
                                {d}
                            </button>
                        ))}
                        <button
                            onClick={next}
                            className="h-[36px] rounded-xl border border-dashed border-[rgba(255,255,255,0.1)] px-4 text-[13px] text-[rgba(255,255,255,0.2)]"
                        >
                            Skip
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="e.g. Computer Science, Business..."
                        value={field}
                        onChange={(e) => setField(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && next()}
                        className={`w-full ${darkInput} focus:border-[rgba(78,174,122,0.25)]`}
                    />
                )}

                {step === 3 && (
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <p className="text-ink-4 mb-1 text-[10px] font-semibold uppercase tracking-wider">
                                From
                            </p>
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="numeric"
                                placeholder="2014"
                                value={startYear}
                                onChange={(e) => setStartYear(e.target.value.slice(0, 4))}
                                onKeyDown={(e) => e.key === "Enter" && finish()}
                                className={`w-full text-center text-[18px] ${darkInput} focus:border-[rgba(78,174,122,0.25)]`}
                            />
                        </div>
                        <span className="text-ink-4 mt-5 text-[14px]">—</span>
                        <div className="flex-1">
                            <p className="text-ink-4 mb-1 text-[10px] font-semibold uppercase tracking-wider">
                                To
                            </p>
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="2018"
                                value={endYear}
                                onChange={(e) => setEndYear(e.target.value.slice(0, 4))}
                                onKeyDown={(e) => e.key === "Enter" && finish()}
                                className={`w-full text-center text-[18px] ${darkInput} focus:border-[rgba(78,174,122,0.25)]`}
                            />
                        </div>
                    </div>
                )}
            </StepFlow>
            <div className="mt-3 flex items-center gap-2">
                {step > 0 && (
                    <button
                        onClick={() => setStep(step - 1)}
                        className="flex h-[36px] items-center rounded-xl border border-[rgba(255,255,255,0.06)] px-3 text-[12px] text-[rgba(255,255,255,0.35)] active:bg-[rgba(255,255,255,0.04)]"
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={step === TOTAL - 1 ? finish : next}
                    disabled={!canNext}
                    className="flex h-[36px] flex-1 items-center justify-center gap-1.5 rounded-xl transition-all active:scale-[0.98]"
                    style={{
                        backgroundColor: canNext
                            ? "rgba(78,174,122,0.18)"
                            : "rgba(255,255,255,0.03)",
                        border: canNext
                            ? "1px solid rgba(78,174,122,0.2)"
                            : "1px solid rgba(255,255,255,0.05)",
                    }}
                >
                    {step === TOTAL - 1 ? (
                        <>
                            <Check
                                size={14}
                                style={{
                                    color: canNext ? "#4EAE7A" : "rgba(255,255,255,0.12)",
                                }}
                            />
                            <span
                                className="text-[13px] font-medium"
                                style={{
                                    color: canNext ? "#4EAE7A" : "rgba(255,255,255,0.12)",
                                }}
                            >
                                {initial ? "Save" : "Done"}
                            </span>
                        </>
                    ) : (
                        <span
                            className="text-[13px] font-medium"
                            style={{
                                color: canNext ? "#4EAE7A" : "rgba(255,255,255,0.12)",
                            }}
                        >
                            Next
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}

// ── Step-by-step location form with address autocomplete ──
function AddLocationField({
    onAdd,
    onCancel,
    initial,
}: {
    onAdd: (addr: Address) => void;
    onCancel: () => void;
    initial?: Address;
}) {
    const [step, setStep] = useState(initial ? 1 : 0);
    const [street, setStreet] = useState(initial?.street ?? "");
    const [city, setCity] = useState(initial?.city ?? "");
    const [state, setState] = useState(initial?.state ?? "");
    const [zip, setZip] = useState(initial?.zip ?? "");
    const [label, setLabel] = useState(initial?.label ?? "home");
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState<
        Array<{
            display: string;
            street?: string;
            city?: string;
            state?: string;
            zip?: string;
        }>
    >([]);
    const [gpsLoading, setGpsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const TOTAL = 3; // 0: search/GPS, 1: verify fields, 2: label

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 150);
    }, [step]);

    // Debounced address search via Nominatim
    useEffect(() => {
        if (query.length < 3) {
            setSuggestions([]);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=us`,
                    { headers: { "Accept-Language": "en" } }
                );
                const data = (await res.json()) as Array<{
                    display_name: string;
                    address: {
                        house_number?: string;
                        road?: string;
                        city?: string;
                        town?: string;
                        village?: string;
                        state?: string;
                        postcode?: string;
                    };
                }>;
                setSuggestions(
                    data.map((d) => ({
                        display: d.display_name,
                        street: [d.address.house_number, d.address.road]
                            .filter(Boolean)
                            .join(" "),
                        city: d.address.city ?? d.address.town ?? d.address.village ?? "",
                        state: d.address.state ?? "",
                        zip: d.address.postcode ?? "",
                    }))
                );
            } catch {
                setSuggestions([]);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [query]);

    // GPS autofill
    const handleGPS = useCallback(() => {
        if (!navigator.geolocation) return;
        setGpsLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { latitude, longitude } = pos.coords;
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
                        { headers: { "Accept-Language": "en" } }
                    );
                    const data = await res.json();
                    const addr = data.address ?? {};
                    setStreet([addr.house_number, addr.road].filter(Boolean).join(" "));
                    setCity(addr.city ?? addr.town ?? addr.village ?? addr.hamlet ?? "");
                    setState(addr.state ?? "");
                    setZip(addr.postcode ?? "");
                    setLabel("current");
                    setStep(1);
                } catch {
                } finally {
                    setGpsLoading(false);
                }
            },
            () => setGpsLoading(false),
            { enableHighAccuracy: true, timeout: 8000 }
        );
    }, []);

    const finish = useCallback(
        (overrideLabel?: string) => {
            if (!city.trim() && !street.trim()) return;
            onAdd({
                label: overrideLabel ?? label,
                street: street.trim() || undefined,
                city: city.trim() || undefined,
                state: state.trim() || undefined,
                zip: zip.trim() || undefined,
            });
        },
        [label, street, city, state, zip, onAdd]
    );

    const next = () => {
        if (step < TOTAL - 1) setStep(step + 1);
        else finish();
    };
    const canNext =
        step === 0
            ? true
            : step === 1
              ? city.trim().length > 0 || street.trim().length > 0
              : true;
    const labels = ["Search address", "Verify details", "Label this location"];
    const labelOptions = [
        { key: "home", label: "Home" },
        { key: "work", label: "Work" },
        { key: "current", label: "Current" },
        { key: "office", label: "Office" },
        { key: "school", label: "School" },
    ];

    return (
        <div className="px-3 py-3">
            <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <MapPin size={13} className="text-[#E8675A]" />
                    <p className="text-[12px] font-medium text-[#E8675A]">
                        {initial ? "Edit" : "Add location"}
                    </p>
                </div>
                <div className="flex items-center gap-1">
                    {step === 0 && (
                        <button
                            onClick={handleGPS}
                            disabled={gpsLoading}
                            className="flex items-center gap-1 rounded-full border border-[rgba(232,103,90,0.2)] bg-[rgba(232,103,90,0.08)] px-2.5 py-1 text-[11px] font-medium text-[#E8675A] active:scale-95"
                        >
                            {gpsLoading ? (
                                <span className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-[#E8675A] border-t-transparent" />
                            ) : (
                                <MapPin size={11} />
                            )}
                            GPS
                        </button>
                    )}
                    <button
                        onClick={onCancel}
                        className="flex h-[26px] w-[26px] items-center justify-center rounded-full active:bg-[rgba(255,255,255,0.06)]"
                    >
                        <X size={14} className="text-ink-4" />
                    </button>
                </div>
            </div>
            <StepFlow steps={TOTAL} currentStep={step}>
                <p className="text-ink-3 mb-2.5 text-[13px] font-light">{labels[step]}</p>

                {/* Step 0: Search or type address */}
                {step === 0 && (
                    <div>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Type an address..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setStreet(query);
                                    setStep(1);
                                }
                            }}
                            className={`w-full ${darkInput} focus:border-[rgba(232,103,90,0.25)]`}
                        />
                        {suggestions.length > 0 && (
                            <div className="mt-1.5 max-h-[180px] overflow-y-auto rounded-xl border border-[rgba(232,103,90,0.12)] bg-[rgba(232,103,90,0.04)] py-1">
                                {suggestions.map((s, i) => (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setStreet(s.street ?? "");
                                            setCity(s.city ?? "");
                                            setState(s.state ?? "");
                                            setZip(s.zip ?? "");
                                            setSuggestions([]);
                                            setQuery("");
                                            setStep(1);
                                        }}
                                        className="flex w-full items-start gap-2 px-3 py-2.5 text-left active:bg-[rgba(255,255,255,0.04)]"
                                    >
                                        <MapPin
                                            size={12}
                                            className="mt-0.5 shrink-0 text-[#E8675A]"
                                        />
                                        <p className="text-[12px] leading-tight text-[rgba(255,255,255,0.8)]">
                                            {s.display}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        )}
                        {suggestions.length === 0 && (
                            <button
                                onClick={() => setStep(1)}
                                className="mt-2 flex items-center gap-1 text-[11px] text-[rgba(255,255,255,0.3)] active:text-[rgba(255,255,255,0.5)]"
                            >
                                <Pencil size={10} /> Enter manually instead
                            </button>
                        )}
                    </div>
                )}

                {/* Step 1: Verify / edit fields */}
                {step === 1 && (
                    <div className="space-y-1.5">
                        <input
                            ref={inputRef}
                            type="text"
                            autoComplete="street-address"
                            placeholder="Street"
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && next()}
                            className={`w-full ${darkInput} focus:border-[rgba(232,103,90,0.25)]`}
                        />
                        <div className="flex gap-1.5">
                            <input
                                type="text"
                                autoComplete="address-level2"
                                placeholder="City"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && next()}
                                className={`min-w-0 flex-1 ${darkInput} focus:border-[rgba(232,103,90,0.25)]`}
                            />
                            <input
                                type="text"
                                autoComplete="address-level1"
                                placeholder="State"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && next()}
                                className={`w-[70px] shrink-0 ${darkInput} focus:border-[rgba(232,103,90,0.25)]`}
                            />
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="postal-code"
                                placeholder="ZIP"
                                value={zip}
                                onChange={(e) => setZip(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && next()}
                                className={`w-[70px] shrink-0 ${darkInput} focus:border-[rgba(232,103,90,0.25)]`}
                            />
                        </div>
                    </div>
                )}

                {/* Step 2: Label */}
                {step === 2 && (
                    <div className="flex flex-wrap gap-1.5">
                        {labelOptions.map((l) => (
                            <button
                                key={l.key}
                                onClick={() => {
                                    setLabel(l.key);
                                    setTimeout(() => finish(l.key), 150);
                                }}
                                className="h-[36px] rounded-xl px-4 text-[13px] font-medium transition-all active:scale-95"
                                style={{
                                    backgroundColor:
                                        label === l.key
                                            ? "rgba(232,103,90,0.15)"
                                            : "rgba(255,255,255,0.04)",
                                    color:
                                        label === l.key
                                            ? "#E8675A"
                                            : "rgba(255,255,255,0.3)",
                                    border:
                                        label === l.key
                                            ? "1px solid rgba(232,103,90,0.2)"
                                            : "1px solid rgba(255,255,255,0.06)",
                                }}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                )}
            </StepFlow>
            <div className="mt-3 flex items-center gap-2">
                {step > 0 && (
                    <button
                        onClick={() => setStep(step - 1)}
                        className="flex h-[36px] items-center rounded-xl border border-[rgba(255,255,255,0.06)] px-3 text-[12px] text-[rgba(255,255,255,0.35)] active:bg-[rgba(255,255,255,0.04)]"
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={step === TOTAL - 1 ? () => finish() : next}
                    disabled={!canNext}
                    className="flex h-[36px] flex-1 items-center justify-center gap-1.5 rounded-xl transition-all active:scale-[0.98]"
                    style={{
                        backgroundColor: canNext
                            ? "rgba(232,103,90,0.18)"
                            : "rgba(255,255,255,0.03)",
                        border: canNext
                            ? "1px solid rgba(232,103,90,0.2)"
                            : "1px solid rgba(255,255,255,0.05)",
                    }}
                >
                    {step === TOTAL - 1 ? (
                        <>
                            <Check
                                size={14}
                                style={{
                                    color: canNext ? "#E8675A" : "rgba(255,255,255,0.12)",
                                }}
                            />
                            <span
                                className="text-[13px] font-medium"
                                style={{
                                    color: canNext ? "#E8675A" : "rgba(255,255,255,0.12)",
                                }}
                            >
                                {initial ? "Save" : "Done"}
                            </span>
                        </>
                    ) : (
                        <span
                            className="text-[13px] font-medium"
                            style={{
                                color: canNext ? "#E8675A" : "rgba(255,255,255,0.12)",
                            }}
                        >
                            Next
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}

// ── Month names and days ──
const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
const MONTH_FULL = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
function daysInMonth(month: number, year: number): number {
    return new Date(year, month + 1, 0).getDate();
}

// ── Editable chip section (skills / tags) ──
function ChipSection({
    title,
    color,
    items,
    onAdd,
    onRemove,
    editable,
    placeholder,
    limit,
    subtitle,
}: {
    title: string;
    color: string;
    items: string[];
    onAdd?: (value: string) => void;
    onRemove?: (index: number) => void;
    editable?: boolean;
    placeholder?: string;
    limit?: number;
    subtitle?: string;
}) {
    const [adding, setAdding] = useState(false);
    const [value, setValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (adding) setTimeout(() => inputRef.current?.focus(), 100);
    }, [adding]);

    const atLimit = limit ? items.length >= limit : false;

    const handleAdd = () => {
        const trimmed = value.trim().toLowerCase();
        if (!trimmed || items.includes(trimmed) || atLimit) return;
        onAdd?.(trimmed);
        setValue("");
        // Stay in adding mode for rapid entry
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    if (items.length === 0 && !editable) return null;

    return (
        <div className="mb-4">
            <div className="mb-2 flex items-center gap-2 px-3">
                <div
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: color }}
                />
                <p
                    className="text-[10px] font-bold uppercase tracking-[0.15em]"
                    style={{ color }}
                >
                    {title}
                </p>
                {subtitle && (
                    <span className="text-[9px] text-[rgba(255,255,255,0.25)]">
                        {subtitle}
                    </span>
                )}
                {limit && (
                    <span className="text-[9px] text-[rgba(255,255,255,0.15)]">
                        {items.length}/{limit}
                    </span>
                )}
            </div>
            <div className="flex flex-wrap gap-1.5 px-3">
                {items.map((item, i) => (
                    <span
                        key={item}
                        className="flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-medium"
                        style={{
                            backgroundColor: `${color}10`,
                            color: `${color}cc`,
                            border: `1px solid ${color}25`,
                        }}
                    >
                        {item}
                        {editable && onRemove && (
                            <button
                                onClick={() => onRemove(i)}
                                className="ml-0.5 flex h-[14px] w-[14px] items-center justify-center rounded-full active:scale-90"
                                style={{ backgroundColor: `${color}20` }}
                            >
                                <X size={8} style={{ color }} />
                            </button>
                        )}
                    </span>
                ))}
                {editable &&
                    !atLimit &&
                    (adding ? (
                        <div className="flex items-center gap-1">
                            <input
                                ref={inputRef}
                                type="text"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleAdd();
                                    if (e.key === "Escape") {
                                        setAdding(false);
                                        setValue("");
                                    }
                                    if (
                                        e.key === "Backspace" &&
                                        !value &&
                                        items.length > 0
                                    ) {
                                        onRemove?.(items.length - 1);
                                    }
                                }}
                                onBlur={() => {
                                    if (value.trim()) handleAdd();
                                    else setAdding(false);
                                }}
                                placeholder={placeholder}
                                className="h-[28px] w-[100px] rounded-full border px-2.5 text-[11px] font-medium text-white placeholder:text-[rgba(255,255,255,0.2)] focus:outline-none"
                                style={{
                                    borderColor: `${color}30`,
                                    backgroundColor: `${color}08`,
                                }}
                            />
                        </div>
                    ) : (
                        <button
                            onClick={() => setAdding(true)}
                            className="flex h-[28px] items-center gap-1 rounded-full border border-dashed px-2.5 text-[11px] font-medium active:scale-95"
                            style={{
                                borderColor: `${color}20`,
                                color: `${color}60`,
                            }}
                        >
                            <Plus size={10} />
                            {items.length === 0
                                ? (placeholder?.replace("...", "") ?? "Add")
                                : "Add"}
                        </button>
                    ))}
            </div>
        </div>
    );
}

// ── Step-by-step birthday wizard ──
function AddBirthdayField({
    onAdd,
    onCancel,
    initial,
}: {
    onAdd: (dateStr: string) => void;
    onCancel: () => void;
    initial?: string; // "YYYY-MM-DD"
}) {
    const initM = initial ? parseInt(initial.slice(5, 7), 10) - 1 : -1;
    const initD = initial ? parseInt(initial.slice(8, 10), 10) : 0;
    const initY = initial ? parseInt(initial.slice(0, 4), 10) : 0;

    const [step, setStep] = useState(initial ? 0 : 0);
    const [month, setMonth] = useState(initM);
    const [day, setDay] = useState(initD);
    const [year, setYear] = useState(initY);
    const [yearInput, setYearInput] = useState(initY ? String(initY) : "");
    const inputRef = useRef<HTMLInputElement>(null);
    const currentYear = new Date().getFullYear();
    const TOTAL = 3; // 0: month, 1: day, 2: year
    const maxDays = month >= 0 ? daysInMonth(month, year || 2000) : 31;

    useEffect(() => {
        if (step === 2) setTimeout(() => inputRef.current?.focus(), 150);
    }, [step]);

    const finish = useCallback(() => {
        if (month < 0) return;
        const m = String(month + 1).padStart(2, "0");
        const d = String(day || 1).padStart(2, "0");
        const y = year || 1990;
        onAdd(`${y}-${m}-${d}`);
    }, [month, day, year, onAdd]);

    const next = () => {
        if (step < TOTAL - 1) setStep(step + 1);
        else finish();
    };

    const canNext = step === 0 ? month >= 0 : true;
    const labels = ["What month?", "What day?", "What year?"];
    const color = "#D4728C";

    return (
        <div className="px-3 py-3">
            <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <Cake size={13} style={{ color }} />
                    <p className="text-[12px] font-medium" style={{ color }}>
                        {initial ? "Edit birthday" : "Add birthday"}
                    </p>
                </div>
                <button
                    onClick={onCancel}
                    className="flex h-[26px] w-[26px] items-center justify-center rounded-full active:bg-[rgba(255,255,255,0.06)]"
                >
                    <X size={14} className="text-ink-4" />
                </button>
            </div>
            <StepFlow steps={TOTAL} currentStep={step}>
                <p className="text-ink-3 mb-2.5 text-[13px] font-light">{labels[step]}</p>

                {/* Step 0: Month */}
                {step === 0 && (
                    <div className="grid grid-cols-4 gap-1.5">
                        {MONTH_FULL.map((_name, i) => (
                            <button
                                key={i}
                                onClick={() => {
                                    setMonth(i);
                                    setTimeout(() => next(), 150);
                                }}
                                className="rounded-xl py-2.5 text-center text-[13px] font-medium transition-all active:scale-95"
                                style={{
                                    backgroundColor:
                                        month === i
                                            ? "rgba(212,114,140,0.15)"
                                            : "rgba(255,255,255,0.04)",
                                    color: month === i ? color : "rgba(255,255,255,0.3)",
                                    border:
                                        month === i
                                            ? "1px solid rgba(212,114,140,0.2)"
                                            : "1px solid rgba(255,255,255,0.06)",
                                }}
                            >
                                {MONTHS[i]}
                            </button>
                        ))}
                    </div>
                )}

                {/* Step 1: Day */}
                {step === 1 && (
                    <div className="flex flex-wrap gap-1.5">
                        {Array.from({ length: maxDays }, (_, i) => i + 1).map((d) => (
                            <button
                                key={d}
                                onClick={() => {
                                    setDay(d);
                                    setTimeout(() => next(), 150);
                                }}
                                className="flex h-[40px] w-[40px] items-center justify-center rounded-xl text-[14px] font-medium transition-all active:scale-90"
                                style={{
                                    backgroundColor:
                                        day === d
                                            ? "rgba(212,114,140,0.15)"
                                            : "rgba(255,255,255,0.04)",
                                    color: day === d ? color : "rgba(255,255,255,0.35)",
                                    border:
                                        day === d
                                            ? "1px solid rgba(212,114,140,0.25)"
                                            : "1px solid transparent",
                                }}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                )}

                {/* Step 2: Year */}
                {step === 2 && (
                    <div className="flex flex-col items-center">
                        <input
                            ref={inputRef}
                            type="text"
                            inputMode="numeric"
                            placeholder="e.g. 1992"
                            value={yearInput}
                            onChange={(e) => {
                                const v = e.target.value.slice(0, 4);
                                setYearInput(v);
                                const n = parseInt(v, 10);
                                if (n >= 1900 && n <= currentYear) setYear(n);
                            }}
                            onKeyDown={(e) => e.key === "Enter" && finish()}
                            className={`w-[140px] text-center text-[22px] ${darkInput} focus:border-[rgba(212,114,140,0.3)]`}
                        />
                        {month >= 0 && day > 0 && yearInput.length === 4 && (
                            <p
                                className="mt-2 text-[13px] font-light"
                                style={{ color: "rgba(212,114,140,0.6)" }}
                            >
                                {MONTH_FULL[month]} {day}, {yearInput}
                            </p>
                        )}
                    </div>
                )}
            </StepFlow>
            <div className="mt-3 flex items-center gap-2">
                {step > 0 && (
                    <button
                        onClick={() => setStep(step - 1)}
                        className="flex h-[36px] items-center rounded-xl border border-[rgba(255,255,255,0.06)] px-3 text-[12px] text-[rgba(255,255,255,0.35)] active:bg-[rgba(255,255,255,0.04)]"
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={step === TOTAL - 1 ? finish : next}
                    disabled={!canNext}
                    className="flex h-[36px] flex-1 items-center justify-center gap-1.5 rounded-xl transition-all active:scale-[0.98]"
                    style={{
                        backgroundColor: canNext
                            ? "rgba(212,114,140,0.18)"
                            : "rgba(255,255,255,0.03)",
                        border: canNext
                            ? "1px solid rgba(212,114,140,0.2)"
                            : "1px solid rgba(255,255,255,0.05)",
                    }}
                >
                    {step === TOTAL - 1 ? (
                        <>
                            <Check
                                size={14}
                                style={{
                                    color: canNext ? color : "rgba(255,255,255,0.12)",
                                }}
                            />
                            <span
                                className="text-[13px] font-medium"
                                style={{
                                    color: canNext ? color : "rgba(255,255,255,0.12)",
                                }}
                            >
                                {initial ? "Save" : "Done"}
                            </span>
                        </>
                    ) : (
                        <span
                            className="text-[13px] font-medium"
                            style={{
                                color: canNext ? color : "rgba(255,255,255,0.12)",
                            }}
                        >
                            Next
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}

// ── Generic add button ──
function AddFieldButton({
    label,
    color,
    onClick,
}: {
    label: string;
    color: string;
    onClick?: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className="flex w-full items-center gap-3 px-3 py-2.5 active:bg-[rgba(255,255,255,0.04)]"
        >
            <div
                className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px]"
                style={{ backgroundColor: `${color}10` }}
            >
                <Plus size={16} style={{ color }} />
            </div>
            <p className="text-[14px] font-medium" style={{ color, opacity: 0.7 }}>
                {label}
            </p>
        </button>
    );
}

// ── Section card wrapper ──
function DetailCard({
    title,
    color,
    children,
    addLabel,
    editable,
    addContent,
    onAddClick,
}: {
    title: string;
    color: string;
    children: React.ReactNode;
    addLabel?: string;
    editable?: boolean;
    addContent?: React.ReactNode;
    onAddClick?: () => void;
}) {
    return (
        <div className="mb-4">
            <div className="mb-1 flex items-center gap-2 px-3">
                <div
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: color }}
                />
                <p
                    className="text-[10px] font-bold uppercase tracking-[0.15em]"
                    style={{ color }}
                >
                    {title}
                </p>
            </div>
            <div className="overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)]">
                {children}
                {addContent ? (
                    <>
                        <div className="mx-3 border-t border-[rgba(255,255,255,0.04)]" />
                        {addContent}
                    </>
                ) : (
                    editable &&
                    addLabel && (
                        <>
                            <div className="mx-3 border-t border-[rgba(255,255,255,0.04)]" />
                            <AddFieldButton
                                label={addLabel}
                                color={color}
                                onClick={onAddClick}
                            />
                        </>
                    )
                )}
            </div>
        </div>
    );
}

// ── Details tab: immersive colored design ──
function DetailsSection({
    c,
    onSave,
}: {
    c: Contact;
    onSave?: (contactId: string, updates: Partial<Contact>) => void;
}) {
    const editable = !c.claimed;

    const [phones, setPhones] = useState<PhoneType[]>(c.phones);
    const [emails, setEmails] = useState<EmailType[]>(c.emails);
    const [socials, setSocials] = useState<Social[]>(c.socials);
    const [addresses, setAddresses] = useState<Address[]>(() => {
        // Merge currentCity into addresses as an editable entry
        const initial = [...c.addresses];
        if (c.currentCity && !initial.some((a) => a.label === "current")) {
            initial.unshift({
                label: "current",
                city: c.currentCity,
                state: c.currentState,
            });
        }
        return initial;
    });
    const [birthday, setBirthday] = useState<string | undefined>(c.birthday);

    // Edit state: null = not editing, { index } = editing existing, "new" = adding new
    const [contactEdit, setContactEdit] = useState<
        { index: number; type: "phone" | "email" } | "new" | null
    >(null);
    const [socialEdit, setSocialEdit] = useState<{ index: number } | "new" | null>(null);
    const [locationEdit, setLocationEdit] = useState<{ index: number } | "new" | null>(
        null
    );
    const [jobs, setJobs] = useState<Job[]>(c.jobs);
    const [education, setEducation] = useState<Education[]>(c.education);
    const [skills, setSkills] = useState<string[]>(c.skills);
    const [tags, setTags] = useState<string[]>(c.tags);
    const [privateTags, setPrivateTags] = useState<string[]>([]);
    const [jobEdit, setJobEdit] = useState<{ index: number } | "new" | null>(null);
    const [eduEdit, setEduEdit] = useState<{ index: number } | "new" | null>(null);
    const [addingBirthday, setAddingBirthday] = useState(false);

    // Auto-save: persist changes whenever any field updates
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        onSave?.(c.id, {
            phones,
            emails,
            socials,
            addresses,
            birthday,
            jobs,
            education,
            skills,
            tags,
            currentCity: addresses.find((a) => a.label === "current")?.city,
            currentState: addresses.find((a) => a.label === "current")?.state,
        });
    }, [phones, emails, socials, addresses, birthday, jobs, education, skills, tags]);

    // Compute initial value for contact edit
    const contactInitial =
        contactEdit && contactEdit !== "new"
            ? contactEdit.type === "phone"
                ? {
                      type: "phone" as const,
                      value: phones[contactEdit.index]?.number ?? "",
                      label: phones[contactEdit.index]?.label ?? "mobile",
                  }
                : {
                      type: "email" as const,
                      value: emails[contactEdit.index]?.address ?? "",
                      label: emails[contactEdit.index]?.label ?? "personal",
                  }
            : undefined;

    const socialInitial =
        socialEdit && socialEdit !== "new" ? socials[socialEdit.index] : undefined;

    const locationInitial =
        locationEdit && locationEdit !== "new"
            ? addresses[locationEdit.index]
            : undefined;

    const jobInitial = jobEdit && jobEdit !== "new" ? jobs[jobEdit.index] : undefined;
    const eduInitial =
        eduEdit && eduEdit !== "new" ? education[eduEdit.index] : undefined;

    const currentJobs = jobs.filter((j) => j.current);
    const pastJobsList = jobs.filter((j) => !j.current);

    const hasLocation = addresses.length > 0;

    return (
        <div className="px-4 pt-4">
            {/* ── Birthday (first — it's important) ── */}
            {(birthday || editable) && (
                <DetailCard
                    title="Birthday"
                    color="#D4728C"
                    editable={editable}
                    addContent={
                        editable && addingBirthday ? (
                            <AddBirthdayField
                                initial={birthday}
                                onAdd={(date) => {
                                    setBirthday(date);
                                    setAddingBirthday(false);
                                }}
                                onCancel={() => setAddingBirthday(false)}
                            />
                        ) : undefined
                    }
                    addLabel={!birthday ? "Add birthday" : undefined}
                    onAddClick={() => setAddingBirthday(true)}
                >
                    {birthday && !addingBirthday && (
                        <SwipeRow
                            enabled={editable}
                            onEdit={() => setAddingBirthday(true)}
                            onDelete={() => setBirthday(undefined)}
                        >
                            <DetailRow
                                icon={Cake}
                                iconColor="#D4728C"
                                iconBg="rgba(212,114,140,0.1)"
                                value={formatBirthday(birthday)}
                                label="birthday"
                                editable={editable}
                            />
                        </SwipeRow>
                    )}
                </DetailCard>
            )}

            {/* ── Contact ── */}
            {(phones.length > 0 || emails.length > 0 || editable) && (
                <DetailCard
                    title="Contact"
                    color="#5B9BD5"
                    addLabel="Add phone or email"
                    editable={editable}
                    onAddClick={() => setContactEdit("new")}
                    addContent={
                        contactEdit ? (
                            <AddContactField
                                initial={contactInitial}
                                onAdd={(type, val, lbl) => {
                                    if (contactEdit !== "new") {
                                        // Replace existing
                                        if (contactEdit.type === "phone") {
                                            setPhones((prev) =>
                                                prev.map((p, j) =>
                                                    j === contactEdit.index
                                                        ? {
                                                              ...p,
                                                              number: val,
                                                              label: lbl,
                                                          }
                                                        : p
                                                )
                                            );
                                        } else {
                                            setEmails((prev) =>
                                                prev.map((e, j) =>
                                                    j === contactEdit.index
                                                        ? {
                                                              ...e,
                                                              address: val,
                                                              label: lbl,
                                                          }
                                                        : e
                                                )
                                            );
                                        }
                                    } else {
                                        // Add new
                                        if (type === "phone") {
                                            setPhones((prev) => [
                                                ...prev,
                                                { label: lbl, number: val },
                                            ]);
                                        } else {
                                            setEmails((prev) => [
                                                ...prev,
                                                { label: lbl, address: val },
                                            ]);
                                        }
                                    }
                                    setContactEdit(null);
                                }}
                                onCancel={() => setContactEdit(null)}
                            />
                        ) : undefined
                    }
                >
                    {phones.map((p, i) => (
                        <SwipeRow
                            key={`p${i}`}
                            enabled={editable}
                            onEdit={() => setContactEdit({ index: i, type: "phone" })}
                            onDelete={() =>
                                setPhones((prev) => prev.filter((_, j) => j !== i))
                            }
                        >
                            <DetailRow
                                icon={Phone}
                                iconColor="#5B9BD5"
                                iconBg="rgba(91,155,213,0.1)"
                                value={formatPhoneDisplay(p.number)}
                                label={p.label}
                                editable={editable}
                                primary={p.primary && c.claimed}
                                href={`tel:${p.number}`}
                            />
                        </SwipeRow>
                    ))}
                    {emails.map((e, i) => (
                        <SwipeRow
                            key={`e${i}`}
                            enabled={editable}
                            onEdit={() => setContactEdit({ index: i, type: "email" })}
                            onDelete={() =>
                                setEmails((prev) => prev.filter((_, j) => j !== i))
                            }
                        >
                            <DetailRow
                                icon={Mail}
                                iconColor="#3AACB5"
                                iconBg="rgba(58,172,181,0.1)"
                                value={e.address}
                                label={e.label}
                                editable={editable}
                                href={`mailto:${e.address}`}
                            />
                        </SwipeRow>
                    ))}
                </DetailCard>
            )}

            {/* ── Socials ── */}
            {(socials.length > 0 || editable) && (
                <DetailCard
                    title="Social"
                    color="#A07FD8"
                    addLabel="Add social link"
                    editable={editable}
                    onAddClick={() => setSocialEdit("new")}
                    addContent={
                        socialEdit ? (
                            <AddSocialField
                                initial={socialInitial}
                                onAdd={(social) => {
                                    if (socialEdit !== "new") {
                                        setSocials((prev) =>
                                            prev.map((s, j) =>
                                                j === socialEdit.index ? social : s
                                            )
                                        );
                                    } else {
                                        setSocials((prev) => [...prev, social]);
                                    }
                                    setSocialEdit(null);
                                }}
                                onCancel={() => setSocialEdit(null)}
                            />
                        ) : undefined
                    }
                >
                    {socials.map((s, i) => (
                        <SwipeRow
                            key={i}
                            enabled={editable}
                            onEdit={() => setSocialEdit({ index: i })}
                            onDelete={() =>
                                setSocials((prev) => prev.filter((_, j) => j !== i))
                            }
                        >
                            <SocialDetailRow social={s} />
                        </SwipeRow>
                    ))}
                </DetailCard>
            )}

            {/* ── Location ── */}
            {(hasLocation || editable) && (
                <DetailCard
                    title="Location"
                    color="#E8675A"
                    addLabel="Add location"
                    editable={editable}
                    onAddClick={() => setLocationEdit("new")}
                    addContent={
                        locationEdit ? (
                            <AddLocationField
                                initial={locationInitial}
                                onAdd={(addr) => {
                                    if (locationEdit !== "new") {
                                        setAddresses((prev) =>
                                            prev.map((a, j) =>
                                                j === locationEdit.index ? addr : a
                                            )
                                        );
                                    } else {
                                        setAddresses((prev) => [...prev, addr]);
                                    }
                                    setLocationEdit(null);
                                }}
                                onCancel={() => setLocationEdit(null)}
                            />
                        ) : undefined
                    }
                >
                    {addresses.map((a, i) => (
                        <SwipeRow
                            key={i}
                            enabled={editable}
                            onEdit={() => setLocationEdit({ index: i })}
                            onDelete={() =>
                                setAddresses((prev) => prev.filter((_, j) => j !== i))
                            }
                        >
                            <DetailRow
                                icon={MapPin}
                                iconColor="#E8675A"
                                iconBg="rgba(232,103,90,0.1)"
                                value={[a.street, a.city, a.state, a.zip]
                                    .filter(Boolean)
                                    .join(", ")}
                                label={a.label}
                                editable={editable}
                                href={buildMapsUrl(a)}
                            />
                        </SwipeRow>
                    ))}
                </DetailCard>
            )}

            {/* ── Career ── */}
            {(jobs.length > 0 || editable) && (
                <DetailCard
                    title="Career"
                    color="#C9A84C"
                    addLabel="Add job"
                    editable={editable}
                    onAddClick={() => setJobEdit("new")}
                    addContent={
                        jobEdit ? (
                            <AddJobField
                                initial={jobInitial}
                                onAdd={(job) => {
                                    if (jobEdit !== "new") {
                                        setJobs((prev) =>
                                            prev.map((j, k) =>
                                                k === jobEdit.index ? job : j
                                            )
                                        );
                                    } else {
                                        setJobs((prev) => [...prev, job]);
                                    }
                                    setJobEdit(null);
                                }}
                                onCancel={() => setJobEdit(null)}
                            />
                        ) : undefined
                    }
                >
                    {currentJobs.map((j, i) => {
                        const idx = jobs.indexOf(j);
                        return (
                            <SwipeRow
                                key={`c${i}`}
                                enabled={editable}
                                onEdit={() => setJobEdit({ index: idx })}
                                onDelete={() =>
                                    setJobs((prev) => prev.filter((_, k) => k !== idx))
                                }
                            >
                                <DetailRow
                                    icon={Briefcase}
                                    iconColor="#C9A84C"
                                    iconBg="rgba(201,168,76,0.1)"
                                    value={`${j.title} at ${j.company}`}
                                    sub={
                                        j.startDate
                                            ? `Since ${j.startDate.slice(0, 4)}`
                                            : undefined
                                    }
                                    label={j.industry ?? ""}
                                    editable={editable}
                                />
                            </SwipeRow>
                        );
                    })}
                    {pastJobsList.map((j, i) => {
                        const idx = jobs.indexOf(j);
                        return (
                            <SwipeRow
                                key={`p${i}`}
                                enabled={editable}
                                onEdit={() => setJobEdit({ index: idx })}
                                onDelete={() =>
                                    setJobs((prev) => prev.filter((_, k) => k !== idx))
                                }
                            >
                                <DetailRow
                                    icon={Briefcase}
                                    iconColor="rgba(201,168,76,0.5)"
                                    iconBg="rgba(201,168,76,0.06)"
                                    value={`${j.title} at ${j.company}`}
                                    sub={j.industry}
                                    label={
                                        j.endDate
                                            ? `until ${j.endDate.slice(0, 4)}`
                                            : "past"
                                    }
                                    editable={editable}
                                />
                            </SwipeRow>
                        );
                    })}
                </DetailCard>
            )}

            {/* ── Education ── */}
            {(education.length > 0 || editable) && (
                <DetailCard
                    title="Education"
                    color="#4EAE7A"
                    addLabel="Add education"
                    editable={editable}
                    onAddClick={() => setEduEdit("new")}
                    addContent={
                        eduEdit ? (
                            <AddEducationField
                                initial={eduInitial}
                                onAdd={(edu) => {
                                    if (eduEdit !== "new") {
                                        setEducation((prev) =>
                                            prev.map((e, k) =>
                                                k === eduEdit.index ? edu : e
                                            )
                                        );
                                    } else {
                                        setEducation((prev) => [...prev, edu]);
                                    }
                                    setEduEdit(null);
                                }}
                                onCancel={() => setEduEdit(null)}
                            />
                        ) : undefined
                    }
                >
                    {education.map((e, i) => (
                        <SwipeRow
                            key={i}
                            enabled={editable}
                            onEdit={() => setEduEdit({ index: i })}
                            onDelete={() =>
                                setEducation((prev) => prev.filter((_, k) => k !== i))
                            }
                        >
                            <DetailRow
                                icon={GraduationCap}
                                iconColor="#4EAE7A"
                                iconBg="rgba(78,174,122,0.1)"
                                value={e.school}
                                sub={[e.degree, e.field].filter(Boolean).join(" in ")}
                                label={
                                    e.startYear && e.endYear
                                        ? `${e.startYear}–${e.endYear}`
                                        : (e.endYear?.toString() ??
                                          e.year?.toString() ??
                                          "")
                                }
                                editable={editable}
                            />
                        </SwipeRow>
                    ))}
                </DetailCard>
            )}

            {/* ── Skills ── */}
            <ChipSection
                title="Skills"
                color="#5B9BD5"
                items={skills}
                onAdd={(s) => setSkills((prev) => [...prev, s])}
                onRemove={(i) => setSkills((prev) => prev.filter((_, j) => j !== i))}
                editable={editable}
                placeholder="Add skill..."
                limit={20}
            />

            {/* ── Tags ── */}
            {c.claimed ? (
                <>
                    {/* Public tags — read-only, controlled by them */}
                    {tags.length > 0 && (
                        <ChipSection
                            title="Public Tags"
                            color="#C9A84C"
                            items={tags}
                            editable={false}
                            limit={5}
                        />
                    )}
                    {/* Private tags — your local labels for this person */}
                    <ChipSection
                        title="My Tags"
                        color="#A07FD8"
                        items={privateTags}
                        onAdd={(t) => setPrivateTags((prev) => [...prev, t])}
                        onRemove={(i) =>
                            setPrivateTags((prev) => prev.filter((_, j) => j !== i))
                        }
                        editable={true}
                        placeholder="Add private tag..."
                        limit={8}
                        subtitle="Only you see these"
                    />
                </>
            ) : (
                <ChipSection
                    title="Tags"
                    color="#C9A84C"
                    items={tags}
                    onAdd={(t) => setTags((prev) => [...prev, t])}
                    onRemove={(i) => setTags((prev) => prev.filter((_, j) => j !== i))}
                    editable={editable}
                    placeholder="Add tag..."
                    limit={8}
                />
            )}
        </div>
    );
}

// ── Groups tab: circles/groups the person belongs to ──
function GroupsSection({ c }: { c: Contact }) {
    const groups = c.tags.map((tag) => ({
        name: tag,
        memberCount: Math.floor(Math.random() * 20) + 3,
    }));

    return (
        <div className="px-5 pt-4">
            <SectionLabel>Circles</SectionLabel>

            {groups.length > 0 ? (
                <div className="flex flex-col gap-2">
                    {groups.map((group) => (
                        <div
                            key={group.name}
                            className="bg-card border-border flex items-center gap-3 rounded-2xl border p-4"
                        >
                            <div className="bg-secondary flex h-[40px] w-[40px] items-center justify-center rounded-full">
                                <CircleDot size={18} className="text-[#A07FD8]" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-foreground text-[15px] font-medium capitalize">
                                    {group.name}
                                </p>
                                <p className="text-ink-4 text-[12px]">
                                    {group.memberCount} members
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CircleDot size={32} className="text-ink-4 mb-3" />
                    <p className="text-ink-3 text-[15px] font-light">
                        {c.firstName} isn't in any groups yet.
                    </p>
                    <p className="text-ink-4 mt-1 text-[13px]">
                        Add them to a circle to organize your network.
                    </p>
                </div>
            )}
        </div>
    );
}

// ── Interactions tab ──
function InteractionsSection({ c }: { c: Contact }) {
    const [interactions, setInteractions] = useState<ProcessedInteraction[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<ProcessedInteraction | null>(null);

    useEffect(() => {
        let cancelled = false;
        if (!c.appleContactId) {
            setInteractions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        listInteractionsForContact(c.appleContactId)
            .then((rows) => {
                if (!cancelled) setInteractions(rows);
            })
            .catch(() => {
                if (!cancelled) setInteractions([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [c.appleContactId]);

    const totalCount = interactions.length;
    const lastSeen =
        interactions.length > 0
            ? formatRelativeDate(interactions[0].createdAt)
            : "---";

    return (
        <div className="px-5 pt-4">
            <div className="flex gap-6 pb-5">
                <div>
                    <div className="text-foreground text-[24px] font-light">
                        {totalCount}
                    </div>
                    <div className="text-ink-4 mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">
                        Total
                    </div>
                </div>
                <div>
                    <div className="text-foreground text-[24px] font-light">
                        {lastSeen}
                    </div>
                    <div className="text-ink-4 mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">
                        Last seen
                    </div>
                </div>
                <div>
                    <div className="text-foreground text-[24px] font-light">
                        {c.source}
                    </div>
                    <div className="text-ink-4 mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">
                        Source
                    </div>
                </div>
            </div>

            <Separator className="mb-4" />

            {loading && interactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-ink-4 text-[13px]">Loading…</p>
                </div>
            ) : interactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <MessageSquare size={32} className="text-ink-4 mb-3" />
                    <p className="text-ink-3 text-[15px] font-light">
                        Nothing yet with {c.firstName}.
                    </p>
                    <p className="text-ink-4 mt-1 text-[13px]">
                        Your conversations together will live here.
                    </p>
                </div>
            ) : (
                <div className="flex flex-col">
                    {interactions.map((i, idx) => (
                        <InteractionRow
                            key={i.id}
                            interaction={i}
                            isLast={idx === interactions.length - 1}
                            onTap={() => setSelected(i)}
                        />
                    ))}
                </div>
            )}

            {interactions.length > 0 && c.appleContactId && (
                <ForgetVoiceLink
                    contactId={c.appleContactId}
                    firstName={c.firstName}
                />
            )}

            {selected && (
                <InteractionDetail
                    interaction={selected}
                    contacts={[c]}
                    onClose={() => setSelected(null)}
                />
            )}
        </div>
    );
}

/**
 * Subtle link — only appears when the contact has interactions. The
 * one place voice management lives in the UI, written as a human
 * sentence rather than a settings toggle. Two-tap confirm because
 * window.confirm() is a no-op in Tauri WKWebView.
 */
function ForgetVoiceLink({
    contactId,
    firstName,
}: {
    contactId: string;
    firstName: string;
}) {
    const [armed, setArmed] = useState(false);
    const [done, setDone] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        []
    );

    const handleTap = async () => {
        if (done) return;
        if (!armed) {
            setArmed(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setArmed(false), 4000);
            return;
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        try {
            await forgetContactVoiceprint(contactId);
            setDone(true);
            setArmed(false);
        } catch {
            setArmed(false);
        }
    };

    if (done) {
        return (
            <p className="mt-8 px-1 text-center text-[11px] text-[rgba(255,255,255,0.3)]">
                Their voice has been forgotten.
            </p>
        );
    }

    return (
        <button
            onClick={handleTap}
            className={`mt-8 w-full px-1 py-2 text-center text-[12px] transition-colors ${
                armed
                    ? "text-[rgba(255,140,140,0.85)]"
                    : "text-[rgba(255,255,255,0.25)] active:text-[rgba(255,255,255,0.5)]"
            }`}
        >
            {armed
                ? `Tap again to let Meridian forget ${firstName}'s voice.`
                : "Let Meridian forget their voice."}
        </button>
    );
}

/**
 * One row in the interaction list. Prefers the Phase 3k first-person
 * summary ("I talked with mom about…") when available, falling back
 * to the first-line transcript preview while summarization is still
 * running or on devices that can't run Foundation Models. No voiceprint
 * metadata is surfaced — voice recognition is invisible infrastructure.
 */
function InteractionRow({
    interaction,
    isLast,
    onTap,
}: {
    interaction: ProcessedInteraction;
    isLast: boolean;
    onTap: () => void;
}) {
    const minutes = Math.max(1, Math.round(interaction.totalSecondsSpoken / 60));
    const display =
        interaction.summary?.trim() ||
        interaction.transcriptPreview?.trim() ||
        "(no transcript captured for this turn)";

    return (
        <button
            type="button"
            onClick={onTap}
            className={`w-full py-4 text-left transition-all active:bg-[rgba(255,255,255,0.02)] ${
                isLast ? "" : "border-b border-[rgba(255,255,255,0.04)]"
            }`}
        >
            <div className="mb-1 flex items-center justify-between gap-3">
                <span className="text-ink-2 text-[13px]">
                    {formatRelativeDate(interaction.createdAt)}
                </span>
                <span className="text-ink-4 text-[11px] font-medium uppercase tracking-[0.08em]">
                    {minutes} min
                </span>
            </div>
            <p className="text-ink line-clamp-3 text-[14px] leading-relaxed">
                {display}
            </p>
            {interaction.locationLabel && (
                <p className="text-ink-4 mt-1.5 text-[11px]">
                    · {interaction.locationLabel}
                </p>
            )}
        </button>
    );
}

/**
 * Human-readable relative date. Recent dates render as "2 days ago",
 * older ones as "Mar 14". Keeps the interaction feed feeling like a
 * timeline instead of a log.
 */
function formatRelativeDate(iso: string): string {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "---";
    const now = Date.now();
    const diffSec = Math.max(0, (now - date.getTime()) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)} hr ago`;
    if (diffSec < 86400 * 7) return `${Math.round(diffSec / 86400)} days ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Main detail component ──
export function ContactDetail({
    contact,
    activeTab,
    onContactChange,
}: ContactDetailProps) {
    return (
        <div className="bg-background flex min-h-screen flex-col pb-[400px]">
            <div className="pt-[env(safe-area-inset-top)]" />
            {/* Global focus handler — scroll input into view above keyboard */}
            <FocusScrollHelper />

            {activeTab === "overview" && (
                <OverviewSection
                    c={contact}
                    onContactChange={onContactChange}
                    onPhotoUpload={(contactId) => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                                const img = new Image();
                                img.onload = () => {
                                    const canvas = document.createElement("canvas");
                                    const sz = 400;
                                    canvas.width = sz;
                                    canvas.height = sz;
                                    const ctx = canvas.getContext("2d")!;
                                    const crop = Math.min(img.width, img.height);
                                    const sx = (img.width - crop) / 2;
                                    const sy = (img.height - crop) / 2;
                                    ctx.drawImage(img, sx, sy, crop, crop, 0, 0, sz, sz);
                                    const photo = canvas.toDataURL("image/jpeg", 0.8);
                                    onContactChange?.(contactId, { photo });
                                };
                                img.src = reader.result as string;
                            };
                            reader.readAsDataURL(file);
                        };
                        input.click();
                    }}
                    onToggleFavorite={(contactId) => {
                        onContactChange?.(contactId, {
                            favorite: !contact.favorite,
                        });
                    }}
                />
            )}
            {activeTab === "details" && (
                <DetailsSection c={contact} onSave={onContactChange} />
            )}
            {activeTab === "circles" && <GroupsSection c={contact} />}
            {activeTab === "interactions" && <InteractionsSection c={contact} />}
        </div>
    );
}

